import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, CheckCircle2, Plus, Trash2, ArrowLeft, GitMerge, ChevronDown, Search, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, RotateCw } from 'lucide-react';
import documentsApi from '../../../api/documentsApi';
import vendorApi from '../../../api/vendorApi';
import { getDocumentVendorName, getInvoiceDataVendorName } from '../../../utils/getDocumentVendorName';
import { useQuery } from '@tanstack/react-query';
import { BusinessInventoryItem, DocInvoice, InvoiceLineItem, UpdateInvoiceRequest, PriceIncreaseItem, DuplicateInvoiceCandidate, InvoiceData, MergedInvoicePage, QboWritePreflightResponse } from '../../../api/types';
import { getUserFriendlyErrorMessage } from '../../../utils/errorMessages';
import type { PDFDocumentProxy } from 'pdfjs-dist';

interface InvoiceConfirmationModalProps {
    documentType: 'invoice' | 'expenses' | 'bills';
    documentId: string;
    /** When provided, modal uses this immediately and skips the fetch — use when doc was already loaded (e.g. from Documents list) */
    preFetchedInvoice?: DocInvoice;
    invoiceData?: {
        vendor_name?: string;
        amount?: number;
        invoice_date?: string;
        invoice_number?: string;
        due_date?: string;
        line_items?: InvoiceLineItem[];
    };
    onClose: () => void;
    onConfirm: (response?: DocInvoice) => void;
    businessId?: string;
    enableQboPrompt?: boolean;
    embedded?: boolean;
    sourceFilename?: string;
    sourcePreviewPage?: number | null;
    sourcePreviewPages?: number[];
    forceSinglePreviewSource?: boolean;
    tutorialMode?: boolean;
    tutorialPreviewUrl?: string;
    tutorialPreviewMimeType?: string;
    tutorialPreviewTarget?: string;
}

interface PreviewSourceOption {
    key: string;
    label: string;
    filename: string;
    previewPage?: number | null;
    fileId?: string | null;
}

const getPrimaryPreviewFileId = (doc?: DocInvoice | null): string | null => (
    doc?.source_file_id || doc?.file_id || doc?.source_file_ids?.[0] || null
);

const getRelatedPreviewDocIds = (doc?: DocInvoice | null): string[] => {
    if (!doc) return [];
    const mergedPageDocIds = (doc.merged_pages || [])
        .map((page) => page?.source_doc_id || null)
        .filter((value): value is string => Boolean(value));
    return Array.from(new Set([
        doc._id || doc.id || null,
        ...(doc.linked_source_doc_ids || []),
        ...(doc.superseded_doc_ids || []),
        ...(doc.merged_from_doc_ids || []),
        ...mergedPageDocIds,
    ].filter((value): value is string => Boolean(value))));
};

const roundCurrency = (value: number) => Math.round(value * 100) / 100;
const roundQuantity = (value: number) => Math.round(value * 1000) / 1000;

const normalizeRawItemKey = (value: string | undefined | null): string => (
    String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
);

const normalizeRawVendorKey = (value: string | undefined | null): string => (
    String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .toUpperCase()
);

const normalizeVendorIdentity = (value: string | undefined | null): string => (
    String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
);

const deriveUsageUnitPrice = (amount: number, usageQuantity?: number) => (
    typeof usageQuantity === 'number' && usageQuantity > 0
        ? roundCurrency(amount / usageQuantity)
        : undefined
);

const getPositiveNumber = (value: unknown): number | undefined => (
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined
);

const normalizeUnitToken = (value: string | undefined | null): string => (
    String(value || '').trim().toLowerCase()
);

const getUsageUnitConversion = (unit: string | undefined | null): { unit: string; factor: number } => {
    const normalized = normalizeUnitToken(unit);
    if (!normalized) {
        return { unit: '', factor: 1 };
    }
    switch (normalized) {
        case 'each':
        case 'ea':
        case 'ct':
        case 'count':
            return { unit: 'ea', factor: 1 };
        case 'doz':
        case 'dozen':
            return { unit: 'ea', factor: 12 };
        default:
            return { unit: normalized, factor: 1 };
    }
};

const getRotatedDimensions = (width: number, height: number, rotation: number) => {
    const normalized = ((rotation % 360) + 360) % 360;
    if (normalized === 90 || normalized === 270) {
        return { width: height, height: width };
    }
    return { width, height };
};

type LineItemRecalcSource = 'init' | 'purchase' | 'line' | 'usage';

const deriveLineItemValues = (item: InvoiceLineItem, source: LineItemRecalcSource = 'init'): InvoiceLineItem => {
    const purchaseQuantity = getPositiveNumber(item.purchase_quantity ?? item.quantity) ?? 1;
    const packCount = getPositiveNumber(item.pack_count);
    const packSize = getPositiveNumber(item.pack_size);
    const packUnit = normalizeUnitToken(item.pack_unit);
    const hasPackBasis = Boolean(packCount || packSize || packUnit);

    const packBasisQuantity = (packCount ?? 1) * (packSize ?? 1);
    const priceBasisQuantity = hasPackBasis ? purchaseQuantity * packBasisQuantity : purchaseQuantity;

    let lineAmount = typeof item.line_amount === 'number'
        ? item.line_amount
        : (typeof item.amount === 'number' ? item.amount : 0);
    let purchaseUnitPrice = typeof item.purchase_unit_price === 'number'
        ? item.purchase_unit_price
        : (typeof item.unit_price === 'number' ? item.unit_price : 0);

    if (priceBasisQuantity > 0) {
        if (source === 'purchase') {
            lineAmount = roundCurrency(priceBasisQuantity * purchaseUnitPrice);
        } else if (source === 'line' || source === 'init') {
            purchaseUnitPrice = roundCurrency(lineAmount / priceBasisQuantity);
        }
    } else {
        lineAmount = 0;
        purchaseUnitPrice = 0;
    }

    const usageConversion = getUsageUnitConversion(packUnit);
    const derivedPackUsageQuantity = hasPackBasis
        ? roundQuantity(purchaseQuantity * packBasisQuantity * usageConversion.factor)
        : undefined;
    const derivedUsageQuantity = derivedPackUsageQuantity ?? purchaseQuantity;
    const derivedUsageUnit = hasPackBasis
        ? (usageConversion.unit || packUnit || normalizeUnitToken(item.usage_unit || item.comparable_unit))
        : normalizeUnitToken(item.purchase_unit || item.unit || item.usage_unit || item.comparable_unit);

    const usageQuantity = source === 'usage'
        ? (getPositiveNumber(item.usage_quantity) ?? derivedUsageQuantity)
        : derivedUsageQuantity;
    const usageUnit = source === 'usage'
        ? (normalizeUnitToken(item.usage_unit) || derivedUsageUnit)
        : derivedUsageUnit;
    const comparableUnit = normalizeUnitToken(item.comparable_unit) || usageUnit;

    return {
        ...item,
        quantity: purchaseQuantity,
        unit: item.purchase_unit || item.unit || '',
        amount: lineAmount,
        unit_price: purchaseUnitPrice,
        purchase_quantity: purchaseQuantity,
        purchase_unit: item.purchase_unit || item.unit || '',
        line_amount: lineAmount,
        purchase_unit_price: purchaseUnitPrice,
        pack_count: packCount,
        pack_size: packSize,
        pack_unit: packUnit,
        usage_quantity: usageQuantity,
        usage_unit: usageUnit,
        usage_unit_price: deriveUsageUnitPrice(lineAmount, usageQuantity),
        comparable_unit: comparableUnit,
    };
};

const formatDisplayNumber = (value?: number) => (
    typeof value === 'number' && Number.isFinite(value)
        ? `${value}`
        : ''
);

const formatPackagingSummary = (item: InvoiceLineItem) => {
    const count = formatDisplayNumber(item.pack_count);
    const size = formatDisplayNumber(item.pack_size);
    const unit = String(item.pack_unit || '').trim().toLowerCase();

    if (count && size && unit) return `${count} count x ${size} ${unit}`;
    if (count && size) return `${count} count x ${size}`;
    if (count && unit) return `${count} count ${unit}`;
    if (size && unit) return `${size} ${unit}`;
    if (count) return `${count} count`;
    if (size) return size;
    if (unit) return unit;
    return 'Set packaging';
};

const pickPreferredValue = <T,>(existing: T | undefined | null, incoming: T | undefined | null): T | undefined => {
    if (existing === undefined || existing === null) return incoming ?? undefined;
    if (typeof existing === 'string' && existing.trim() === '') return incoming ?? undefined;
    if (typeof existing === 'object' && !Array.isArray(existing) && Object.keys(existing as Record<string, unknown>).length === 0) {
        return incoming ?? undefined;
    }
    return existing;
};

const dedupeStrings = (values: Array<string | null | undefined>) => {
    const seen = new Set<string>();
    const deduped: string[] = [];
    values.forEach((value) => {
        const normalized = String(value || '').trim();
        if (!normalized || seen.has(normalized)) return;
        seen.add(normalized);
        deduped.push(normalized);
    });
    return deduped;
};

const getPreferredVendorName = (data?: InvoiceData | null, fallback?: string | null): string => {
    return getInvoiceDataVendorName(data, fallback);
};

const getDuplicateRelatedCandidate = (doc?: DocInvoice | null) => {
    const relatedCandidates = doc?.related_document_candidates;
    return Array.isArray(relatedCandidates)
        ? relatedCandidates.find((candidate) => candidate.relationship_type === 'duplicate_candidate')
        : undefined;
};

const getPrimaryRelatedCandidate = (doc?: DocInvoice | null) => {
    const relatedCandidates = doc?.related_document_candidates;
    if (!Array.isArray(relatedCandidates) || relatedCandidates.length === 0) return undefined;
    const status = doc?.data?.status;
    const isEligible = (candidateStatus?: string) => (
        status === 'confirmed'
        || status === 'posted_to_qbo'
        || candidateStatus === 'confirmed'
        || candidateStatus === 'posted_to_qbo'
    );
    return (
        relatedCandidates.find((candidate) => candidate.relationship_type === 'combine_candidate' && isEligible(candidate.candidate_status))
        || relatedCandidates.find((candidate) => candidate.relationship_type === 'duplicate_candidate' && isEligible(candidate.candidate_status))
    );
};

const formatRelatedSourcePages = (pages?: number[] | null) => {
    const normalized = (pages || []).filter((page): page is number => Number.isFinite(page)).sort((a, b) => a - b);
    if (normalized.length === 0) return null;
    if (normalized.length === 1) return `page ${normalized[0]}`;
    return `pages ${normalized[0]}-${normalized[normalized.length - 1]}`;
};

const buildLineItemMergeKey = (item: InvoiceLineItem): string => [
    normalizeRawItemKey(item.raw_description || item.description || item.canonical_name),
    String(item.purchase_quantity ?? item.quantity ?? ''),
    normalizeUnitToken(item.purchase_unit || item.unit),
    String(item.line_amount ?? item.amount ?? ''),
].join('|');

const mergeLineItemsWithoutDuplicates = (existingItems: InvoiceLineItem[], incomingItems: InvoiceLineItem[]): InvoiceLineItem[] => {
    const merged = [...existingItems];
    const seen = new Set(existingItems.map(buildLineItemMergeKey));
    incomingItems.forEach((item) => {
        const key = buildLineItemMergeKey(item);
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(item);
    });
    return merged;
};

const buildLineItemSearchText = (item: InvoiceLineItem): string => (
    [
        item.raw_description,
        item.description,
        item.canonical_name,
        item.purchase_quantity,
        item.purchase_unit,
        item.purchase_unit_price,
        item.pack_count,
        item.pack_size,
        item.pack_unit,
        item.usage_quantity,
        item.usage_unit,
        item.usage_unit_price,
        item.comparable_unit,
        item.line_amount,
        item.amount,
        formatPackagingSummary(item),
    ]
        .filter((value) => value !== undefined && value !== null && `${value}`.trim() !== '')
        .map((value) => `${value}`.toLowerCase())
        .join(' ')
);

const BASIC_UNIT_OPTIONS = ['', 'ea', 'each', 'ct', 'doz', 'cs', 'case', 'ctn', 'box', 'bag', 'pack', 'lb', 'oz', 'qt', 'gal', 'pt', 'l', 'ml'];

const normalizeLineItemsForForm = (items: InvoiceLineItem[] = []): InvoiceLineItem[] => (
    items.map((item) => {
        const quantity = typeof item.quantity === 'number' && item.quantity > 0 ? item.quantity : 1;
        const amount = typeof item.amount === 'number' ? item.amount : 0;
        const unitPrice = typeof item.unit_price === 'number'
            ? item.unit_price
            : quantity > 0
                ? roundCurrency(amount / quantity)
                : 0;
        return deriveLineItemValues({
            ...item,
            raw_description: item.raw_description || item.description || item.canonical_name || '',
            description: item.description || item.canonical_name || item.raw_description || '',
            canonical_name: item.canonical_name || item.description || item.raw_description || '',
            quantity,
            unit: item.unit || '',
            unit_price: unitPrice,
            amount,
            purchase_quantity: typeof item.purchase_quantity === 'number' && item.purchase_quantity > 0 ? item.purchase_quantity : quantity,
            purchase_unit: item.purchase_unit || item.unit || '',
            line_amount: typeof item.line_amount === 'number' ? item.line_amount : amount,
            purchase_unit_price: typeof item.purchase_unit_price === 'number' ? item.purchase_unit_price : unitPrice,
            pack_count: typeof item.pack_count === 'number' ? item.pack_count : undefined,
            pack_size: typeof item.pack_size === 'number' ? item.pack_size : undefined,
            pack_unit: item.pack_unit || '',
            usage_quantity: typeof item.usage_quantity === 'number' && item.usage_quantity > 0 ? item.usage_quantity : undefined,
            usage_unit: item.usage_unit || item.comparable_unit || '',
            usage_unit_price: typeof item.usage_unit_price === 'number'
                ? item.usage_unit_price
                : deriveUsageUnitPrice(amount, item.usage_quantity),
            comparable_unit: item.comparable_unit || item.usage_unit || '',
            comparable_unit_family: item.comparable_unit_family || '',
            normalization_status: item.normalization_status || 'review_ready',
            normalization_source: item.normalization_source || 'review',
        }, 'init');
    })
);

const formatInventoryItemDisplayName = (item?: Pick<BusinessInventoryItem, 'canonical_name' | 'brand_name'> | null) => {
    const canonicalName = String(item?.canonical_name || '').trim();
    const brandName = String(item?.brand_name || '').trim();
    if (canonicalName && brandName) return `${canonicalName} - ${brandName}`;
    return canonicalName || 'Untitled item';
};

const getRawLineItems = (data?: Partial<InvoiceData> | null): InvoiceLineItem[] => (
    Array.isArray(data?.line_items) ? (data?.line_items as InvoiceLineItem[]) : []
);

const summarizeRawAmount = (data?: Partial<InvoiceData> | null): number => {
    if (typeof data?.total_amount === 'number') return data.total_amount;
    return getRawLineItems(data).reduce((sum, item) => sum + (item.amount || 0), 0);
};

interface SuggestionOption {
    id: string;
    name: string;
    subtitle?: string;
}

interface SearchableSuggestionInputProps {
    value: string;
    options: SuggestionOption[];
    onChangeText: (value: string) => void;
    onSelectOption: (option: SuggestionOption) => void;
    placeholder?: string;
    inputClassName?: string;
    hideChevron?: boolean;
}

const SearchableSuggestionInput: React.FC<SearchableSuggestionInputProps> = ({
    value,
    options,
    onChangeText,
    onSelectOption,
    placeholder,
    inputClassName,
    hideChevron = false,
}) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const filteredOptions = useMemo(() => {
        const q = value.trim().toLowerCase();
        const baseOptions = !q
            ? options.slice(0, 50)
            : options
                .filter((option) => (
                    option.name.toLowerCase().includes(q)
                    || option.subtitle?.toLowerCase().includes(q)
                ))
                .slice(0, 50);

        const hasExactMatch = options.some((option) => option.name.trim().toLowerCase() === q);
        if (!q || hasExactMatch) return baseOptions;

        return [
            {
                id: '__typed__',
                name: value.trim(),
                subtitle: 'Use current value',
            },
            ...baseOptions,
        ];
    }, [options, value]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div ref={containerRef} className="relative">
            <input
                type="text"
                value={value}
                onFocus={() => setOpen(true)}
                onClick={() => setOpen(true)}
                onChange={(e) => {
                    onChangeText(e.target.value);
                    setOpen(true);
                }}
                placeholder={placeholder}
                className={inputClassName || "w-full px-3 py-2.5 pr-10 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"}
            />
            {!hideChevron && (
                <ChevronDown className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            )}
            {open && (
                <div className="absolute top-[calc(100%+6px)] left-0 right-0 z-20 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
                    <div className="max-h-64 overflow-y-auto">
                        {filteredOptions.length > 0 ? filteredOptions.map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                onClick={() => {
                                    onSelectOption(option);
                                    setOpen(false);
                                }}
                                className="w-full border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-gray-50"
                            >
                                <div className="truncate text-sm font-medium text-gray-900">{option.name}</div>
                                {option.subtitle && (
                                    <div className="truncate text-xs text-gray-500">{option.subtitle}</div>
                                )}
                            </button>
                        )) : (
                            <div className="px-3 py-3 text-sm text-gray-500">No matching options</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const InvoiceConfirmationModal: React.FC<InvoiceConfirmationModalProps> = ({
    documentType,
    documentId,
    preFetchedInvoice,
    invoiceData: initialInvoiceData,
    onClose,
    onConfirm,
    businessId,
    enableQboPrompt = false,
    embedded = false,
    sourceFilename,
    sourcePreviewPage,
    sourcePreviewPages,
    forceSinglePreviewSource = false,
    tutorialMode = false,
    tutorialPreviewUrl,
    tutorialPreviewMimeType = 'image/png',
    tutorialPreviewTarget,
}) => {
    const isBillDocument = documentType === 'bills';
    const isExpenseDocument = documentType === 'expenses';
    const isQboSyncEligibleDocument = !isExpenseDocument;
    const documentLabel = isBillDocument ? 'bill' : (isExpenseDocument ? 'expense' : 'invoice');
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(!preFetchedInvoice);
    const [error, setError] = useState<string | null>(null);
    const [invoice, setInvoice] = useState<DocInvoice | null>(preFetchedInvoice ?? null);
    const [confirmedResponse, setConfirmedResponse] = useState<DocInvoice | null>(null);
    const [showPriceChangePopup, setShowPriceChangePopup] = useState(false);
    const [priceIncreaseItems, setPriceIncreaseItems] = useState<PriceIncreaseItem[]>([]);
    const [priceDecreaseItems, setPriceDecreaseItems] = useState<PriceIncreaseItem[]>([]);
    const [showDuplicateDecision, setShowDuplicateDecision] = useState(false);
    const [duplicateCandidate, setDuplicateCandidate] = useState<DuplicateInvoiceCandidate | null>(null);
    const [relatedDecisionType, setRelatedDecisionType] = useState<'duplicate_candidate' | 'combine_candidate' | null>(null);
    const [relatedTargetDoc, setRelatedTargetDoc] = useState<DocInvoice | null>(null);
    const [pendingUpdateData, setPendingUpdateData] = useState<UpdateInvoiceRequest | null>(null);
    const [combineTargetDocumentId, setCombineTargetDocumentId] = useState<string | null>(null);
    const [combineSourceUpdateData, setCombineSourceUpdateData] = useState<UpdateInvoiceRequest | null>(null);
    const [linkMode, setLinkMode] = useState<'combine' | 'duplicate' | null>(null);
    const [showQboWriteDecision, setShowQboWriteDecision] = useState(false);
    const [pendingQboUpdateData, setPendingQboUpdateData] = useState<UpdateInvoiceRequest | null>(null);
    const [qboPreflight, setQboPreflight] = useState<QboWritePreflightResponse | null>(null);
    const [editorView, setEditorView] = useState<'cleaned' | 'original'>('cleaned');
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewZoom, setPreviewZoom] = useState(1);
    const [previewRotation, setPreviewRotation] = useState(0);
    const [previewPageNumber, setPreviewPageNumber] = useState(1);
    const [previewPageCount, setPreviewPageCount] = useState(1);
    const [resolvedPreviewPages, setResolvedPreviewPages] = useState<number[]>([]);
    const [previewRenderError, setPreviewRenderError] = useState<string | null>(null);
    const [previewMimeType, setPreviewMimeType] = useState<string | null>(null);
    const [previewViewportSize, setPreviewViewportSize] = useState({ width: 0, height: 0 });
    const [previewImageNaturalSize, setPreviewImageNaturalSize] = useState({ width: 0, height: 0 });
    const [previewCanvasDisplaySize, setPreviewCanvasDisplaySize] = useState({ width: 0, height: 0 });
    const [resolvedPreviewSources, setResolvedPreviewSources] = useState<PreviewSourceOption[]>([]);
    const [activePreviewSourceKey, setActivePreviewSourceKey] = useState('current');
    const [previewPdfReadyToken, setPreviewPdfReadyToken] = useState(0);
    const autoPresentedRelatedDecisionRef = useRef<string | null>(null);
    const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const previewViewportRef = useRef<HTMLDivElement | null>(null);
    const previewPdfRef = useRef<PDFDocumentProxy | null>(null);
    const previewObjectUrlRef = useRef<string | null>(null);
    const previewCacheRef = useRef<Map<string, {
        objectUrl: string;
        mimeType: string | null;
        naturalWidth?: number;
        naturalHeight?: number;
    }>>(new Map());

    // Form state — init from preFetchedInvoice when available
    const initFromDoc = useCallback((doc: DocInvoice) => {
        const data = doc.data;
        setInvoice(doc);
        setVendorName(getPreferredVendorName(data));
        setInvoiceNumber(data.invoice_number || '');
        const txDate = data.transaction_date;
        // Use extracted date as-is (including today); do not clear when it matches the current calendar date.
        setTransactionDate(txDate ? String(txDate) : '');
        setDueDate(data.due_date || '');
        setTotalAmount(data.total_amount || 0);
        setTotalTax(Number(data.tax_details?.total_tax || 0));
        setCurrency(data.currency || 'USD');
        setLineItems(normalizeLineItemsForForm(data.line_items || []));
    }, []);

    const [vendorName, setVendorName] = useState(
        preFetchedInvoice ? getPreferredVendorName(preFetchedInvoice.data, initialInvoiceData?.vendor_name) : ''
    );
    const [invoiceNumber, setInvoiceNumber] = useState(preFetchedInvoice ? (preFetchedInvoice.data?.invoice_number || '') : '');
    const [transactionDate, setTransactionDate] = useState(() => {
        const tx = preFetchedInvoice?.data?.transaction_date;
        return tx ? String(tx) : '';
    });
    const [dueDate, setDueDate] = useState(preFetchedInvoice ? (preFetchedInvoice.data?.due_date || '') : '');
    const [totalAmount, setTotalAmount] = useState(preFetchedInvoice ? (preFetchedInvoice.data?.total_amount || 0) : 0);
    const [totalTax, setTotalTax] = useState(preFetchedInvoice ? Number(preFetchedInvoice.data?.tax_details?.total_tax || 0) : 0);
    const [currency, setCurrency] = useState(preFetchedInvoice ? (preFetchedInvoice.data?.currency || 'USD') : 'USD');
    const [lineItemSearch, setLineItemSearch] = useState('');
    const [lineItems, setLineItems] = useState<InvoiceLineItem[]>(
        preFetchedInvoice ? normalizeLineItemsForForm(preFetchedInvoice.data?.line_items || []) : []
    );

    const { data: inventoryItemsResponse } = useQuery({
        queryKey: ['businessInventoryItems', businessId],
        queryFn: () => vendorApi.getInventoryItems(undefined, 1, 200, businessId),
        enabled: !!businessId,
        staleTime: 60_000,
    });
    const inventoryItems: BusinessInventoryItem[] = [...(inventoryItemsResponse?.items || [])].sort((a, b) => (
        (a.canonical_name || '').localeCompare(b.canonical_name || '')
    ));

    const { data: vendorAliasesResponse } = useQuery({
        queryKey: ['confirmedVendorAliases', businessId],
        queryFn: () => vendorApi.getAliases('verified', undefined, 1, 200, 'updated_at', 'desc', 'outflow', undefined, businessId),
        enabled: !!businessId,
        staleTime: 60_000,
    });

    const vendorOptions = Array.from(
        new Set(
            (vendorAliasesResponse?.items || [])
                .map((alias) => (alias.clean_vendor_name || '').trim())
                .filter(Boolean)
        )
    ).sort((a, b) => a.localeCompare(b)).map((name) => ({
        id: name,
        name,
    }));

    const filteredLineItems = useMemo(() => {
        const query = lineItemSearch.trim().toLowerCase();
        return lineItems
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => !query || buildLineItemSearchText(item).includes(query));
    }, [lineItemSearch, lineItems]);

    const fetchDocumentById = useCallback(async (id: string): Promise<DocInvoice> => {
        return documentType === 'bills'
            ? documentsApi.getBill(id, businessId)
            : (documentType === 'expenses'
                ? documentsApi.getExpense(id, businessId)
                : documentsApi.getInvoice(id, businessId));
    }, [businessId, documentType]);

    const buildCombinedPreviewDoc = useCallback((targetDoc: DocInvoice, sourceUpdate: UpdateInvoiceRequest): DocInvoice => {
        const targetData = targetDoc.data;
        const sourceData = sourceUpdate.data;
        const mergedLineItems = mergeLineItemsWithoutDuplicates(
            normalizeLineItemsForForm(targetData.line_items || []),
            normalizeLineItemsForForm(sourceData.line_items || []),
        );
        const targetTotal = Number(targetData.total_amount || 0);
        const sourceTotal = Number(sourceData.total_amount || 0);
        const targetBalance = Number(targetData.balance_due || targetTotal);
        const sourceBalance = Number(sourceData.balance_due || sourceTotal);
        const targetTax = Number(targetData.tax_details?.total_tax || 0);
        const sourceTax = Number(sourceData.tax_details?.total_tax || 0);
        const now = new Date().toISOString();

        return {
            ...targetDoc,
            data: {
                ...targetData,
                transaction_date: pickPreferredValue(targetData.transaction_date, sourceData.transaction_date) || '',
                due_date: pickPreferredValue(targetData.due_date, sourceData.due_date),
                invoice_number: pickPreferredValue(targetData.invoice_number, sourceData.invoice_number),
                total_amount: Math.max(targetTotal, sourceTotal),
                balance_due: Math.max(targetBalance, sourceBalance),
                currency: pickPreferredValue(targetData.currency, sourceData.currency) || 'USD',
                vendor: pickPreferredValue(targetData.vendor, sourceData.vendor) || { name: '' },
                ap_account: pickPreferredValue(targetData.ap_account, sourceData.ap_account),
                line_items: mergedLineItems,
                billing_address: pickPreferredValue(targetData.billing_address, sourceData.billing_address),
                tax_details: { total_tax: Math.max(targetTax, sourceTax) },
                metadata: {
                    create_time: pickPreferredValue(targetData.metadata?.create_time, sourceData.metadata?.create_time) || now,
                    last_updated_time: now,
                    vendor_resolution: pickPreferredValue(targetData.metadata?.vendor_resolution, sourceData.metadata?.vendor_resolution),
                },
                status: 'confirmed',
                extraction_method: pickPreferredValue(targetData.extraction_method, sourceData.extraction_method),
                confidence_scores: pickPreferredValue(targetData.confidence_scores, sourceData.confidence_scores) || {},
            },
        };
    }, []);

    const openRelatedDecision = useCallback(async (
        sourceDoc: DocInvoice,
        relationshipType: 'duplicate_candidate' | 'combine_candidate',
        relatedDocumentId: string,
    ) => {
        let targetDoc: DocInvoice;
        try {
            targetDoc = await fetchDocumentById(relatedDocumentId);
        } catch (err: any) {
            if (String(err?.message || '').toLowerCase().includes('not found')) {
                return;
            }
            throw err;
        }
        setRelatedTargetDoc(targetDoc);
        setRelatedDecisionType(relationshipType);
        setDuplicateCandidate({
            id: relatedDocumentId,
            vendor_name: getDocumentVendorName(targetDoc, 'Unknown vendor'),
            invoice_number: targetDoc.data?.invoice_number,
            transaction_date: targetDoc.data?.transaction_date,
            total_amount: targetDoc.data?.total_amount,
            status: targetDoc.data?.status,
            page_count: targetDoc.page_count || targetDoc.source_pages?.length || targetDoc.merged_pages?.length || 1,
            file_count: targetDoc.source_file_ids?.length || 1,
        });
        setPendingUpdateData({
            data: {
                ...sourceDoc.data,
                vendor: { name: vendorName.trim() || getPreferredVendorName(sourceDoc.data) },
                invoice_number: invoiceNumber.trim() || undefined,
                transaction_date: transactionDate || sourceDoc.data.transaction_date || new Date().toISOString().split('T')[0],
                due_date: dueDate?.trim() || sourceDoc.data.due_date || undefined,
                total_amount: totalAmount || sourceDoc.data.total_amount || 0,
                balance_due: totalAmount || sourceDoc.data.total_amount || 0,
                currency: currency || sourceDoc.data.currency || 'USD',
                line_items: lineItems.length > 0 ? lineItems : normalizeLineItemsForForm(sourceDoc.data.line_items || []),
            } as InvoiceData,
        });
        setShowDuplicateDecision(true);
    }, [currency, dueDate, fetchDocumentById, invoiceNumber, lineItems, totalAmount, transactionDate, vendorName]);

    // Fetch document only when no pre-fetched data
    useEffect(() => {
        const fetchDoc = async () => {
            try {
                if (!preFetchedInvoice) {
                    setFetching(true);
                }
                const doc = documentType === 'bills'
                    ? await documentsApi.getBill(documentId, businessId)
                    : (documentType === 'expenses'
                        ? await documentsApi.getExpense(documentId, businessId)
                        : await documentsApi.getInvoice(documentId, businessId));
                initFromDoc(doc);
            } catch (err: any) {
                console.error('Failed to fetch document:', err);
                setError(getUserFriendlyErrorMessage(err, 'Failed to load data.'));
                if (initialInvoiceData) {
                    setVendorName(String(initialInvoiceData.vendor_name || '').trim());
                    setInvoiceNumber(initialInvoiceData.invoice_number || '');
                    setTransactionDate(initialInvoiceData.invoice_date || '');
                    setDueDate(initialInvoiceData.due_date || '');
                    setTotalAmount(initialInvoiceData.amount || 0);
                    setTotalTax(0);
                    setLineItems(normalizeLineItemsForForm(initialInvoiceData.line_items || []));
                }
            } finally {
                setFetching(false);
            }
        };

        if (preFetchedInvoice) {
            initFromDoc(preFetchedInvoice);
            setFetching(false);
            return;
        }

        if (documentId) {
            fetchDoc();
        } else if (initialInvoiceData) {
            setVendorName(String(initialInvoiceData.vendor_name || '').trim());
            setInvoiceNumber(initialInvoiceData.invoice_number || '');
            setTransactionDate(initialInvoiceData.invoice_date || '');
            setDueDate(initialInvoiceData.due_date || '');
            setTotalAmount(initialInvoiceData.amount || 0);
            setTotalTax(0);
            setLineItems(normalizeLineItemsForForm(initialInvoiceData.line_items || []));
            setFetching(false);
        }
    }, [businessId, documentId, documentType, initialInvoiceData, initFromDoc, preFetchedInvoice]);

    const previewSourceOptions = useMemo<PreviewSourceOption[]>(() => {
        if (resolvedPreviewSources.length > 0) {
            return resolvedPreviewSources;
        }
        if (!sourceFilename) return [];
        return [{
            key: 'current',
            label: `This ${documentLabel}`,
            filename: sourceFilename,
            previewPage: sourcePreviewPage ?? preFetchedInvoice?.preview_page ?? null,
            fileId: preFetchedInvoice?.file_id ?? null,
        }];
    }, [documentLabel, preFetchedInvoice?.file_id, preFetchedInvoice?.preview_page, resolvedPreviewSources, sourceFilename, sourcePreviewPage]);

    const activePreviewSource = useMemo(
        () => previewSourceOptions.find((option) => option.key === activePreviewSourceKey) || previewSourceOptions[0] || null,
        [activePreviewSourceKey, previewSourceOptions]
    );

    const activePreviewSourceIndex = useMemo(
        () => Math.max(0, previewSourceOptions.findIndex((option) => option.key === activePreviewSourceKey)),
        [activePreviewSourceKey, previewSourceOptions]
    );

    const shouldUseSourcePagination = previewSourceOptions.length > 1;
    const displayedPreviewPosition = shouldUseSourcePagination ? activePreviewSourceIndex + 1 : previewPageNumber;
    const displayedPreviewCount = shouldUseSourcePagination ? previewSourceOptions.length : previewPageCount;
    const cyclePreviewRotation = useCallback(() => {
        setPreviewRotation((current) => (current + 90) % 360);
    }, []);

    useEffect(() => {
        const currentDocId = invoice?._id || invoice?.id || documentId;
        if (!invoice || !currentDocId) return;
        if (combineTargetDocumentId || showDuplicateDecision || showQboWriteDecision) return;
        if (autoPresentedRelatedDecisionRef.current === currentDocId) return;

        const relatedCandidate = getPrimaryRelatedCandidate(invoice);
        if (!relatedCandidate?.document_id) return;

        autoPresentedRelatedDecisionRef.current = currentDocId;
        void openRelatedDecision(invoice, relatedCandidate.relationship_type, relatedCandidate.document_id).catch((err) => {
            console.error('Failed to open related document decision:', err);
        });
    }, [combineTargetDocumentId, documentId, invoice, openRelatedDecision, showDuplicateDecision, showQboWriteDecision]);

    useEffect(() => {
        if (!activePreviewSource?.filename) return;
        let cancelled = false;
        const loadPreview = async () => {
            let isImagePreview = false;
            try {
                setPreviewLoading(true);
                setPreviewRenderError(null);
                if (tutorialPreviewUrl) {
                    if (!cancelled) {
                        setPreviewUrl(tutorialPreviewUrl);
                        setPreviewMimeType(tutorialPreviewMimeType);
                        setPreviewZoom(1);
                        setPreviewRotation(0);
                        setPreviewPageNumber(activePreviewSource.previewPage || sourcePreviewPage || 1);
                        setPreviewCanvasDisplaySize({ width: 0, height: 0 });
                    }
                    return;
                }
                const cached = previewCacheRef.current.get(activePreviewSource.filename);
                if (cached) {
                    isImagePreview = Boolean(cached.mimeType?.startsWith('image/'));
                    if (!cancelled) {
                        previewObjectUrlRef.current = cached.objectUrl;
                        setPreviewUrl(cached.objectUrl);
                        setPreviewMimeType(cached.mimeType);
                        setPreviewZoom(1);
                        setPreviewRotation(0);
                        setPreviewPageNumber(activePreviewSource.previewPage || sourcePreviewPage || 1);
                        setPreviewImageNaturalSize({
                            width: cached.naturalWidth || 0,
                            height: cached.naturalHeight || 0,
                        });
                        setPreviewCanvasDisplaySize({ width: 0, height: 0 });
                        if (!isImagePreview) {
                            setPreviewLoading(false);
                        }
                    }
                    return;
                }
                let resolvedFilename = activePreviewSource.filename;
                let blob: Blob;
                try {
                    blob = await documentsApi.getDownloadBlob(resolvedFilename, businessId);
                } catch (initialErr) {
                    if (activePreviewSource.fileId) {
                        const fileMeta = await documentsApi.getUserGlobalFile(activePreviewSource.fileId, businessId);
                        resolvedFilename = fileMeta.filename;
                        blob = await documentsApi.getDownloadBlob(resolvedFilename, businessId);
                    } else {
                        throw initialErr;
                    }
                }
                const nextObjectUrl = URL.createObjectURL(blob);
                isImagePreview = Boolean((blob.type || '').startsWith('image/'));
                let naturalWidth = 0;
                let naturalHeight = 0;
                if (isImagePreview) {
                    const preloadImage = new Image();
                    await new Promise<void>((resolve, reject) => {
                        preloadImage.onload = () => {
                            naturalWidth = preloadImage.naturalWidth || 0;
                            naturalHeight = preloadImage.naturalHeight || 0;
                            resolve();
                        };
                        preloadImage.onerror = () => reject(new Error('Failed to decode image preview.'));
                        preloadImage.src = nextObjectUrl;
                    });
                }
                if (!cancelled) {
                    previewCacheRef.current.set(activePreviewSource.filename, {
                        objectUrl: nextObjectUrl,
                        mimeType: blob.type || null,
                        naturalWidth,
                        naturalHeight,
                    });
                    previewObjectUrlRef.current = nextObjectUrl;
                    setPreviewUrl(nextObjectUrl);
                    setPreviewMimeType(blob.type || null);
                    setPreviewZoom(1);
                    setPreviewRotation(0);
                    setPreviewPageNumber(activePreviewSource.previewPage || sourcePreviewPage || 1);
                    setPreviewImageNaturalSize({ width: naturalWidth, height: naturalHeight });
                    setPreviewCanvasDisplaySize({ width: 0, height: 0 });
                    if (!isImagePreview || (naturalWidth && naturalHeight)) {
                        setPreviewLoading(false);
                    }
                } else {
                    URL.revokeObjectURL(nextObjectUrl);
                }
            } catch (err) {
                console.error('Failed to load document preview:', err);
                if (!cancelled) {
                    setPreviewUrl(null);
                    setPreviewMimeType(null);
                    setPreviewRenderError('Failed to load document preview.');
                }
            } finally {
                if (!cancelled && !isImagePreview) {
                    setPreviewLoading(false);
                }
            }
        };
        void loadPreview();
        return () => {
            cancelled = true;
        };
    }, [activePreviewSource?.filename, activePreviewSource?.previewPage, businessId, sourcePreviewPage, tutorialPreviewMimeType, tutorialPreviewUrl]);

    useEffect(() => {
        return () => {
            previewCacheRef.current.forEach((entry) => {
                URL.revokeObjectURL(entry.objectUrl);
            });
            previewCacheRef.current.clear();
            previewObjectUrlRef.current = null;
        };
    }, []);

    useEffect(() => {
        const isLinkedPreviewMode = Boolean(combineTargetDocumentId || showDuplicateDecision || relatedTargetDoc);
        if (forceSinglePreviewSource) {
            setResolvedPreviewSources(sourceFilename ? [{
                key: 'current',
                label: `This ${documentLabel}`,
                filename: sourceFilename,
                previewPage: sourcePreviewPage ?? preFetchedInvoice?.preview_page ?? null,
                fileId: preFetchedInvoice?.file_id ?? null,
            }] : []);
            return;
        }

        let cancelled = false;
        const loadPreviewSources = async () => {
            try {
                if (isLinkedPreviewMode) {
                    const seedDocs = [preFetchedInvoice || invoice, relatedTargetDoc].filter(Boolean) as DocInvoice[];
                    const relatedDocIds = Array.from(new Set(seedDocs.flatMap((doc) => getRelatedPreviewDocIds(doc))));
                    const knownDocs = new Map<string, DocInvoice>();
                    seedDocs.forEach((doc) => {
                        const docId = doc._id || doc.id;
                        if (docId) {
                            knownDocs.set(docId, doc);
                        }
                    });
                    const missingIds = relatedDocIds.filter((docId) => !knownDocs.has(docId));
                    if (missingIds.length > 0) {
                        const fetchedDocs = await Promise.allSettled(missingIds.map((docId) => fetchDocumentById(docId)));
                        fetchedDocs.forEach((result, index) => {
                            if (result.status === 'fulfilled') {
                                knownDocs.set(missingIds[index], result.value);
                            }
                        });
                    }

                    const orderedDocs = relatedDocIds
                        .map((docId) => knownDocs.get(docId) || null)
                        .filter((doc): doc is DocInvoice => Boolean(doc));

                    const anchorInvoiceNumber = (
                        (preFetchedInvoice || invoice)?.data?.invoice_number
                        || relatedTargetDoc?.data?.invoice_number
                        || null
                    );
                    const filteredDocs = anchorInvoiceNumber
                        ? orderedDocs.filter((doc) => (doc.data?.invoice_number || null) === anchorInvoiceNumber)
                        : orderedDocs;

                    console.log('[Linked Preview] seed docs', seedDocs.map((doc) => ({
                        id: doc._id || doc.id,
                        invoice_number: doc.data?.invoice_number,
                        file_id: doc.file_id,
                        source_file_id: doc.source_file_id,
                        source_file_ids: doc.source_file_ids,
                        linked_source_doc_ids: doc.linked_source_doc_ids,
                        superseded_doc_ids: doc.superseded_doc_ids,
                        merged_from_doc_ids: doc.merged_from_doc_ids,
                        merged_page_doc_ids: (doc.merged_pages || []).map((page) => page?.source_doc_id || null).filter(Boolean),
                    })));
                    console.log('[Linked Preview] related doc ids', relatedDocIds);
                    console.log('[Linked Preview] ordered docs', orderedDocs.map((doc) => ({
                        id: doc._id || doc.id,
                        invoice_number: doc.data?.invoice_number,
                        file_id: doc.file_id,
                        source_file_id: doc.source_file_id,
                        source_file_ids: doc.source_file_ids,
                    })));
                    console.log('[Linked Preview] filtered docs', filteredDocs.map((doc) => ({
                        id: doc._id || doc.id,
                        invoice_number: doc.data?.invoice_number,
                        file_id: doc.file_id,
                        source_file_id: doc.source_file_id,
                        source_file_ids: doc.source_file_ids,
                    })));

                    const sourceEntries = filteredDocs
                        .map((doc, index) => {
                            const fileId = getPrimaryPreviewFileId(doc);
                            if (!fileId) return null;
                            const previewPage = doc.preview_page ?? doc.source_pages?.[0] ?? null;
                            return {
                                doc,
                                fileId,
                                index,
                                previewPage,
                                dedupeKey: `${fileId}:${previewPage ?? 'all'}:${doc._id || doc.id || index}`,
                            };
                        })
                        .filter((entry): entry is {
                            doc: DocInvoice;
                            fileId: string;
                            index: number;
                            previewPage: number | null;
                            dedupeKey: string;
                        } => Boolean(entry));

                    const uniqueSourceEntries = sourceEntries.filter((entry, index, list) => (
                        list.findIndex((candidate) => candidate.dedupeKey === entry.dedupeKey) === index
                    ));

                    console.log('[Linked Preview] source entries', sourceEntries.map((entry) => ({
                        doc_id: entry.doc._id || entry.doc.id,
                        invoice_number: entry.doc.data?.invoice_number,
                        file_id: entry.fileId,
                    })));
                    console.log('[Linked Preview] unique source entries', uniqueSourceEntries.map((entry) => ({
                        doc_id: entry.doc._id || entry.doc.id,
                        invoice_number: entry.doc.data?.invoice_number,
                        file_id: entry.fileId,
                    })));

                    if (uniqueSourceEntries.length === 0) {
                        setResolvedPreviewSources(sourceFilename ? [{
                            key: 'current',
                            label: `This ${documentLabel}`,
                            filename: sourceFilename,
                            previewPage: sourcePreviewPage ?? preFetchedInvoice?.preview_page ?? null,
                            fileId: preFetchedInvoice?.file_id ?? null,
                        }] : []);
                        return;
                    }

                    const files = await Promise.all(uniqueSourceEntries.map((entry) => documentsApi.getUserGlobalFile(entry.fileId, businessId)));
                    if (cancelled) return;
                    const nextOptions = files.map((file, index) => {
                        const sourceEntry = uniqueSourceEntries[index];
                        const doc = sourceEntry.doc;
                        return {
                            key: `${file.file_id || `source-${index}`}:${sourceEntry.previewPage ?? index}:${doc._id || doc.id || index}`,
                            label: `Image ${index + 1}`,
                            filename: file.filename,
                            previewPage: sourceEntry.previewPage,
                            fileId: file.file_id,
                        };
                    });
                    console.log('[Linked Preview] preview source options', nextOptions);
                    setResolvedPreviewSources(nextOptions);
                    setActivePreviewSourceKey((currentKey) => (
                        nextOptions.some((option) => option.key === currentKey)
                            ? currentKey
                            : (nextOptions[0]?.key || 'current')
                    ));
                    return;
                }

                const sourceFileIds = dedupeStrings([
                    preFetchedInvoice?.file_id,
                    preFetchedInvoice?.source_file_id,
                    ...(preFetchedInvoice?.source_file_ids || []),
                    invoice?.file_id,
                    invoice?.source_file_id,
                    ...(invoice?.source_file_ids || []),
                ]);

                if (sourceFileIds.length === 0) {
                    setResolvedPreviewSources(sourceFilename ? [{
                        key: 'current',
                        label: `This ${documentLabel}`,
                        filename: sourceFilename,
                        previewPage: sourcePreviewPage ?? preFetchedInvoice?.preview_page ?? null,
                        fileId: preFetchedInvoice?.file_id ?? null,
                    }] : []);
                    return;
                }

                const files = await Promise.all(sourceFileIds.map((fileId) => documentsApi.getUserGlobalFile(fileId, businessId)));
                if (cancelled) return;
                const nextOptions = files.map((file, index) => {
                    const isCurrentFile = file.file_id === preFetchedInvoice?.file_id || file.file_id === preFetchedInvoice?.source_file_id;
                    const defaultLabel = files.length === 2
                        ? (isCurrentFile ? `This ${documentLabel}` : `Matched ${documentLabel}`)
                        : `Image ${index + 1}`;
                    return {
                        key: file.file_id || `source-${index}`,
                        label: defaultLabel,
                        filename: file.filename,
                        previewPage: (
                            file.file_id === preFetchedInvoice?.file_id
                            || file.file_id === preFetchedInvoice?.source_file_id
                        ) ? (preFetchedInvoice?.preview_page ?? null) : (invoice?.preview_page ?? null),
                        fileId: file.file_id,
                    };
                });
                setResolvedPreviewSources(nextOptions);
                setActivePreviewSourceKey((currentKey) => (
                    nextOptions.some((option) => option.key === currentKey)
                        ? currentKey
                        : (nextOptions[0]?.key || 'current')
                ));
            } catch (err) {
                console.error('Failed to load preview sources:', err);
                if (!cancelled) {
                    setResolvedPreviewSources(sourceFilename ? [{
                        key: 'current',
                        label: `This ${documentLabel}`,
                        filename: sourceFilename,
                        previewPage: preFetchedInvoice?.preview_page ?? null,
                        fileId: preFetchedInvoice?.file_id ?? null,
                    }] : []);
                }
            }
        };
        void loadPreviewSources();
        return () => {
            cancelled = true;
        };
    }, [
        businessId,
        combineTargetDocumentId,
        documentLabel,
        fetchDocumentById,
        invoice?.file_id,
        invoice?.id,
        invoice?.preview_page,
        invoice?.source_file_id,
        invoice?.source_file_ids,
        invoice?._id,
        invoice?.linked_source_doc_ids,
        invoice?.merged_from_doc_ids,
        invoice?.merged_pages,
        invoice?.superseded_doc_ids,
        preFetchedInvoice?.file_id,
        preFetchedInvoice?.id,
        preFetchedInvoice?.preview_page,
        preFetchedInvoice?.source_file_id,
        preFetchedInvoice?.source_file_ids,
        preFetchedInvoice?._id,
        preFetchedInvoice?.linked_source_doc_ids,
        preFetchedInvoice?.merged_from_doc_ids,
        preFetchedInvoice?.merged_pages,
        preFetchedInvoice?.superseded_doc_ids,
        forceSinglePreviewSource,
        relatedTargetDoc?.file_id,
        relatedTargetDoc?.id,
        relatedTargetDoc?.preview_page,
        relatedTargetDoc?.file_id,
        relatedTargetDoc?.source_file_id,
        relatedTargetDoc?._id,
        relatedTargetDoc?.linked_source_doc_ids,
        relatedTargetDoc?.merged_from_doc_ids,
        relatedTargetDoc?.merged_pages,
        relatedTargetDoc?.superseded_doc_ids,
        sourceFilename,
        sourcePreviewPage,
        showDuplicateDecision,
    ]);

    useEffect(() => {
        if (combineTargetDocumentId) {
            const matchedOption = previewSourceOptions.find((option) => option.label.startsWith('Matched '));
            setActivePreviewSourceKey(matchedOption?.key || previewSourceOptions[0]?.key || 'current');
        } else {
            const currentOption = previewSourceOptions.find((option) => option.label.startsWith('This '));
            setActivePreviewSourceKey(currentOption?.key || previewSourceOptions[0]?.key || 'current');
        }
    }, [combineTargetDocumentId, previewSourceOptions]);

    useEffect(() => {
        const explicitPages = sourcePreviewPages?.filter((page) => Number.isFinite(page) && page > 0) || [];
        if (explicitPages.length > 0) {
            setResolvedPreviewPages(explicitPages);
            setPreviewPageNumber(explicitPages[0]);
            return;
        }

        const activeSourcePreviewPage = activePreviewSource?.previewPage || null;
        if (combineTargetDocumentId || showDuplicateDecision || relatedTargetDoc) {
            if (activeSourcePreviewPage && Number.isFinite(activeSourcePreviewPage) && activeSourcePreviewPage > 0) {
                setResolvedPreviewPages([activeSourcePreviewPage]);
                setPreviewPageNumber(1);
                return;
            }
            setResolvedPreviewPages([]);
            setPreviewPageNumber(sourcePreviewPage || invoice?.preview_page || preFetchedInvoice?.preview_page || 1);
            return;
        }

        const fallbackSourcePages = (invoice?.source_pages || preFetchedInvoice?.source_pages || [])
            .filter((page) => Number.isFinite(page) && page > 0);

        if (fallbackSourcePages.length > 0) {
            setResolvedPreviewPages(fallbackSourcePages);
            setPreviewPageNumber(fallbackSourcePages[0]);
            return;
        }

        const singlePreviewPage = sourcePreviewPage || invoice?.preview_page || preFetchedInvoice?.preview_page || null;
        if (singlePreviewPage && Number.isFinite(singlePreviewPage) && singlePreviewPage > 0) {
            setResolvedPreviewPages([singlePreviewPage]);
            setPreviewPageNumber(singlePreviewPage);
            return;
        }

        setResolvedPreviewPages([]);
        setPreviewPageNumber(1);
    }, [
        combineTargetDocumentId,
        invoice?.preview_page,
        invoice?.source_pages,
        preFetchedInvoice?.preview_page,
        preFetchedInvoice?.source_pages,
        activePreviewSource?.previewPage,
        relatedTargetDoc,
        showDuplicateDecision,
        sourcePreviewPage,
        sourcePreviewPages,
    ]);

    useEffect(() => {
        const container = previewViewportRef.current;
        if (!container || typeof ResizeObserver === 'undefined') return;

        const updateSize = () => {
            setPreviewViewportSize({
                width: container.clientWidth,
                height: container.clientHeight,
            });
        };
        updateSize();

        const observer = new ResizeObserver(() => updateSize());
        observer.observe(container);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!previewMimeType?.startsWith('image/')) return;
        if (!previewUrl) return;
        if (!previewImageNaturalSize.width || !previewImageNaturalSize.height) return;
        if (!previewViewportSize.width || !previewViewportSize.height) return;
        setPreviewLoading(false);
    }, [
        previewImageNaturalSize.height,
        previewImageNaturalSize.width,
        previewMimeType,
        previewUrl,
        previewViewportSize.height,
        previewViewportSize.width,
    ]);

    useEffect(() => {
        const isImagePreview = Boolean(previewMimeType && previewMimeType.startsWith('image/'));
        if (isImagePreview) {
            previewPdfRef.current = null;
            setPreviewPdfReadyToken(0);
            setPreviewPageCount(1);
            setPreviewRenderError(null);
            return;
        }
        if (!previewUrl) {
            previewPdfRef.current = null;
            setPreviewPdfReadyToken(0);
            setPreviewPageCount(1);
            return;
        }
        let cancelled = false;

        const loadPdf = async () => {
            try {
                setPreviewLoading(true);
                setPreviewRenderError(null);
                const pdfjs = await import('pdfjs-dist');
                pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
                const loadingTask = pdfjs.getDocument(previewUrl);
                const pdf = await loadingTask.promise;
                if (cancelled) {
                    void loadingTask.destroy();
                    return;
                }
                previewPdfRef.current = pdf as PDFDocumentProxy;
                setPreviewPdfReadyToken((current) => current + 1);
                const availablePages = resolvedPreviewPages.length > 0
                    ? resolvedPreviewPages.filter((page) => page <= (pdf.numPages || 1))
                    : [];
                setPreviewPageCount(availablePages.length > 0 ? availablePages.length : (pdf.numPages || 1));
                setPreviewPageNumber((current) => {
                    if (availablePages.length > 0) {
                        const normalizedIndex = Math.max(1, Math.min(current || 1, availablePages.length));
                        return normalizedIndex;
                    }
                    const preferredPage = sourcePreviewPage || invoice?.preview_page || current || 1;
                    return Math.min(Math.max(preferredPage, 1), pdf.numPages || 1);
                });
            } catch (err) {
                console.error('Failed to load PDF preview:', err);
                if (!cancelled) {
                    previewPdfRef.current = null;
                    setPreviewPdfReadyToken(0);
                    setPreviewPageCount(1);
                    setPreviewRenderError('Preview is unavailable for this document.');
                }
            } finally {
                if (!cancelled) {
                    setPreviewLoading(false);
                }
            }
        };

        void loadPdf();
        return () => {
            cancelled = true;
        };
    }, [invoice?.preview_page, previewMimeType, previewUrl, resolvedPreviewPages, sourcePreviewPage]);

    useEffect(() => {
        const container = previewViewportRef.current;
        const canvas = previewCanvasRef.current;
        const pdf = previewPdfRef.current;
        if (!container || !canvas || !pdf) return;
        if (!previewViewportSize.width || !previewViewportSize.height) return;

        let cancelled = false;
        let resizeObserver: ResizeObserver | null = null;
        let renderToken = 0;
        let activeRenderTask: { cancel?: () => void; promise?: Promise<unknown> } | null = null;

        const renderPage = async () => {
            const currentToken = ++renderToken;
            try {
                activeRenderTask?.cancel?.();
                const targetPdfPage = resolvedPreviewPages.length > 0
                    ? resolvedPreviewPages[Math.min(Math.max(previewPageNumber, 1), previewPageCount) - 1]
                    : Math.min(Math.max(previewPageNumber, 1), previewPageCount);
                const page = await pdf.getPage(targetPdfPage);
                if (cancelled || currentToken !== renderToken) return;

                const unscaledViewport = page.getViewport({ scale: 1 });
                const rotatedBaseSize = getRotatedDimensions(unscaledViewport.width, unscaledViewport.height, previewRotation);
                const widthScale = Math.max((container.clientWidth - 32) / rotatedBaseSize.width, 0.25);
                const fitScale = widthScale;
                const scale = Math.max(fitScale * previewZoom, 0.1);
                const viewport = page.getViewport({ scale });
                const outputScale = window.devicePixelRatio || 1;
                const context = canvas.getContext('2d');
                if (!context) return;

                canvas.width = Math.floor(viewport.width * outputScale);
                canvas.height = Math.floor(viewport.height * outputScale);
                canvas.style.width = `${Math.floor(viewport.width)}px`;
                canvas.style.height = `${Math.floor(viewport.height)}px`;
                setPreviewCanvasDisplaySize({
                    width: Math.floor(viewport.width),
                    height: Math.floor(viewport.height),
                });
                context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
                context.clearRect(0, 0, viewport.width, viewport.height);

                activeRenderTask = page.render({
                    canvasContext: context,
                    viewport,
                });
                await activeRenderTask.promise;
                if (!cancelled && currentToken === renderToken) {
                    setPreviewRenderError(null);
                }
            } catch (err) {
                const errorName = (err as { name?: string } | null)?.name;
                if (errorName === 'RenderingCancelledException') {
                    return;
                }
                console.error('Failed to render PDF page preview:', err);
                if (!cancelled) {
                    setPreviewRenderError('Preview is unavailable for this page.');
                }
            } finally {
                // Keep the last rendered page visible during rerenders.
            }
        };

        void renderPage();
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                void renderPage();
            });
            resizeObserver.observe(container);
        }

        return () => {
            cancelled = true;
            activeRenderTask?.cancel?.();
            resizeObserver?.disconnect();
        };
    }, [
        previewPageCount,
        previewPageNumber,
        previewRotation,
        previewPdfReadyToken,
        previewViewportSize.height,
        previewViewportSize.width,
        previewZoom,
        previewUrl,
        resolvedPreviewPages,
    ]);

    const previewImageDimensions = useMemo(() => {
        if (!previewMimeType?.startsWith('image/')) return null;
        if (!previewImageNaturalSize.width || !previewImageNaturalSize.height || !previewViewportSize.width || !previewViewportSize.height) {
            return null;
        }
        const rotatedNaturalSize = getRotatedDimensions(previewImageNaturalSize.width, previewImageNaturalSize.height, previewRotation);
        const usableWidth = Math.max(previewViewportSize.width - 32, 120);
        const fitScale = usableWidth / rotatedNaturalSize.width;
        const effectiveScale = Math.max(fitScale * previewZoom, 0.1);
        const renderedWidth = Math.round(previewImageNaturalSize.width * effectiveScale);
        const renderedHeight = Math.round(previewImageNaturalSize.height * effectiveScale);
        const rotatedRendered = getRotatedDimensions(renderedWidth, renderedHeight, previewRotation);
        return {
            width: renderedWidth,
            height: renderedHeight,
            rotatedWidth: rotatedRendered.width,
            rotatedHeight: rotatedRendered.height,
            percent: Math.round(effectiveScale * 100),
        };
    }, [previewImageNaturalSize.height, previewImageNaturalSize.width, previewMimeType, previewRotation, previewViewportSize.height, previewViewportSize.width, previewZoom]);

    const previewCanvasRotatedSize = useMemo(() => {
        return getRotatedDimensions(
            previewCanvasDisplaySize.width,
            previewCanvasDisplaySize.height,
            previewRotation,
        );
    }, [previewCanvasDisplaySize.height, previewCanvasDisplaySize.width, previewRotation]);

    const previewImageFrameStyle = useMemo(() => {
        const minWidth = Math.max(previewViewportSize.width - 32, 0);
        const minHeight = Math.max(previewViewportSize.height - 32, 0);
        if (!previewImageDimensions) {
            return {
                width: `${minWidth}px`,
                minHeight: `${minHeight}px`,
            };
        }
        return {
            width: `${Math.max(previewImageDimensions.rotatedWidth, minWidth)}px`,
            height: `${Math.max(previewImageDimensions.rotatedHeight, minHeight)}px`,
        };
    }, [previewImageDimensions, previewViewportSize.height, previewViewportSize.width]);

    const previewCanvasFrameStyle = useMemo(() => {
        const minWidth = Math.max(previewViewportSize.width - 32, 0);
        const minHeight = Math.max(previewViewportSize.height - 32, 0);
        if (!previewCanvasRotatedSize.width || !previewCanvasRotatedSize.height) {
            return {
                width: `${minWidth}px`,
                minHeight: `${minHeight}px`,
            };
        }
        return {
            width: `${Math.max(previewCanvasRotatedSize.width, minWidth)}px`,
            height: `${Math.max(previewCanvasRotatedSize.height, minHeight)}px`,
        };
    }, [previewCanvasRotatedSize.height, previewCanvasRotatedSize.width, previewViewportSize.height, previewViewportSize.width]);

    const syncLineItem = (updatedItem: InvoiceLineItem, source: LineItemRecalcSource = 'init'): InvoiceLineItem => {
        return deriveLineItemValues({
            ...updatedItem,
            description: typeof updatedItem.description === 'string'
                ? updatedItem.description
                : (updatedItem.canonical_name || ''),
            canonical_name: typeof updatedItem.canonical_name === 'string'
                ? updatedItem.canonical_name
                : (updatedItem.description || ''),
            raw_description: updatedItem.raw_description || '',
        }, source);
    };

    const getApprovedInventoryOptions = (currentItem?: InvoiceLineItem | null): BusinessInventoryItem[] => {
        return inventoryItems
            .filter((candidate) => {
                const isCurrentSelection = !!currentItem?.business_item_id && candidate._id === currentItem.business_item_id;
                const isApproved = candidate.needs_review !== true && candidate.status !== 'needs_review' && candidate.status !== 'awaiting_approval';
                return isCurrentSelection || isApproved;
            })
            .sort((a, b) => (a.canonical_name || '').localeCompare(b.canonical_name || ''));
    };

    const getFilteredInventoryOptions = (index: number): BusinessInventoryItem[] => {
        const currentItem = lineItems[index];
        const search = (currentItem?.description || currentItem?.canonical_name || '').trim().toLowerCase();
        return getApprovedInventoryOptions(currentItem)
            .filter((candidate) => {
                if (!search) return true;
                return (candidate.canonical_name || '').toLowerCase().includes(search)
                    || (candidate.search_keys || []).some((key) => key.toLowerCase().includes(search));
            })
            .slice(0, 50);
    };

    const handleInventorySuggestionSelect = (index: number, candidateId: string) => {
        const candidate = inventoryItems.find((item) => item._id === candidateId);
        if (!candidate) return;
        const updated = [...lineItems];
        const nextItem = { ...updated[index] };
        nextItem.business_item_id = candidate._id;
        nextItem.description = candidate.canonical_name;
        nextItem.canonical_name = candidate.canonical_name;
        nextItem.brand_name = candidate.brand_name || '';
        if (!nextItem.comparable_unit_family && candidate.default_usage_unit_family) {
            nextItem.comparable_unit_family = candidate.default_usage_unit_family;
        }
        updated[index] = syncLineItem(nextItem);
        setLineItems(updated);
    };

    const persistReviewedItemAliases = async (items: InvoiceLineItem[]) => {
        if (!businessId) return;
        const requests = items
            .map((item) => {
                const rawNormName = normalizeRawItemKey(item.raw_description || item.description);
                const cleanItemName = (item.description || item.canonical_name || '').trim();
                if (!rawNormName || !cleanItemName) return null;
                return vendorApi.createItemAlias({
                    raw_norm_name: rawNormName,
                    clean_item_name: cleanItemName,
                    brand_name: (item.brand_name || '').trim() || undefined,
                    business_item_id: item.business_item_id,
                    vendor_name: vendorName.trim() || undefined,
                    resolution_status: item.business_item_id ? 'verified' : 'awaiting_approval',
                    needs_review: !item.business_item_id,
                    confidence_score: item.business_item_id ? 1 : 0.7,
                }, businessId);
            })
            .filter(Boolean) as Promise<unknown>[];
        if (requests.length === 0) return;
        await Promise.allSettled(requests);
    };

    const persistReviewedVendorAlias = async () => {
        if (!businessId) return;
        const rawVendorName = (
            invoice?.original_parsed_data?.vendor?.name
            || preFetchedInvoice?.original_parsed_data?.vendor?.name
            || initialInvoiceData?.vendor_name
            || invoice?.data?.vendor?.raw_name
            || invoice?.data?.metadata?.vendor_resolution?.raw_vendor_name
            || invoice?.data?.vendor?.name
        )?.trim();
        const cleanVendorName = vendorName.trim();
        const rawNormName = normalizeRawVendorKey(rawVendorName);
        if (!rawNormName || !cleanVendorName) return;
        const vendorResolution = invoice?.data?.metadata?.vendor_resolution
            || preFetchedInvoice?.data?.metadata?.vendor_resolution;
        const resolutionMatchesCurrentVendor = normalizeVendorIdentity(cleanVendorName) === normalizeVendorIdentity(
            String(vendorResolution?.clean_vendor_name || vendorResolution?.raw_vendor_name || '')
        );
        await vendorApi.createAlias({
            raw_norm_name: rawNormName,
            clean_vendor_name: cleanVendorName,
            direction: 'outflow',
            is_user_verified: true,
            finz_data: resolutionMatchesCurrentVendor ? vendorResolution?.finz_data : undefined,
            suggested_qbo_account_id: resolutionMatchesCurrentVendor ? vendorResolution?.suggested_qbo_account_id : undefined,
            suggested_qbo_account_name: resolutionMatchesCurrentVendor ? vendorResolution?.suggested_qbo_account_name : undefined,
            suggested_qbo_account_source: resolutionMatchesCurrentVendor ? vendorResolution?.suggested_qbo_account_source : undefined,
            suggested_qbo_account_confidence: resolutionMatchesCurrentVendor ? vendorResolution?.suggested_qbo_account_confidence : undefined,
        }, businessId);
    };

    const handleAddLineItem = () => {
        setLineItems([...lineItems, {
            description: '',
            canonical_name: '',
            brand_name: '',
            raw_description: '',
            amount: 0,
            quantity: 1,
            unit: '',
            unit_price: 0,
            purchase_quantity: 1,
            purchase_unit: '',
            line_amount: 0,
            purchase_unit_price: 0,
            usage_unit: '',
            comparable_unit: '',
            tax_code: ''
        }]);
    };

    const handleRemoveLineItem = (index: number) => {
        const updated = lineItems.filter((_, i) => i !== index);
        setLineItems(updated);
        setTotalAmount(roundCurrency(updated.reduce((sum, item) => sum + (item.line_amount ?? item.amount ?? 0), 0)));
    };

    const handleLineItemChange = (index: number, field: keyof InvoiceLineItem, value: string | number) => {
        const updated = [...lineItems];
        const nextItem = { ...updated[index], [field]: value };
        let recalcSource: LineItemRecalcSource = 'line';

        if (field === 'amount' || field === 'line_amount') {
            nextItem.line_amount = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
            nextItem.amount = nextItem.line_amount;
            recalcSource = 'line';
        } else if (field === 'unit_price' || field === 'purchase_unit_price') {
            nextItem.purchase_unit_price = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
            nextItem.unit_price = nextItem.purchase_unit_price;
            recalcSource = 'purchase';
        } else if (field === 'description' || field === 'canonical_name') {
            const nextName = String(value || '');
            nextItem.description = nextName;
            nextItem.canonical_name = nextName;
            const exactMatch = getApprovedInventoryOptions(updated[index]).find((candidate) => (
                (candidate.canonical_name || '').trim().toLowerCase() === nextName.trim().toLowerCase()
            ));
            nextItem.business_item_id = exactMatch?._id;
            recalcSource = 'init';
        } else if (field === 'purchase_unit') {
            nextItem.unit = String(value || '');
            recalcSource = 'line';
        } else if (field === 'quantity') {
            nextItem.purchase_quantity = typeof value === 'number' ? value : parseFloat(String(value)) || 1;
            recalcSource = 'line';
        } else if (field === 'purchase_quantity' || field === 'pack_count' || field === 'pack_size' || field === 'pack_unit') {
            recalcSource = 'line';
        } else if (field === 'usage_unit') {
            nextItem.comparable_unit = String(value || '');
            recalcSource = 'usage';
        } else if (field === 'usage_quantity' || field === 'comparable_unit') {
            recalcSource = 'usage';
        }

        updated[index] = syncLineItem(nextItem, recalcSource);
        setLineItems(updated);
        const newSum = updated.reduce((sum, item) => sum + (item.line_amount ?? item.amount ?? 0), 0);
        setTotalAmount(roundCurrency(newSum));
    };

    const buildUpdateData = (): UpdateInvoiceRequest | null => {
        if (!vendorName || totalAmount < 0) {
            setError('Please fill in all required fields (Vendor, Amount)');
            return null;
        }

        const finalTransactionDate = transactionDate || new Date().toISOString().split('T')[0];
        const finalLineItems = lineItems.map((item) => {
            const synced = syncLineItem(item);
            const finalDescription = (synced.description || synced.canonical_name || synced.raw_description || '').trim();
            const finalCanonicalName = (synced.canonical_name || synced.description || synced.raw_description || '').trim();
            return {
                ...synced,
                description: finalDescription,
                canonical_name: finalCanonicalName,
                brand_name: (synced.brand_name || '').trim() || undefined,
                raw_description: (synced.raw_description || synced.description || '').trim(),
                quantity: synced.purchase_quantity ?? synced.quantity ?? 1,
                unit: synced.purchase_unit || synced.unit || '',
                amount: synced.line_amount ?? synced.amount ?? 0,
                unit_price: synced.purchase_unit_price ?? synced.unit_price ?? 0,
                purchase_quantity: synced.purchase_quantity ?? synced.quantity ?? 1,
                purchase_unit: synced.purchase_unit || synced.unit || '',
                line_amount: synced.line_amount ?? synced.amount ?? 0,
                purchase_unit_price: synced.purchase_unit_price ?? synced.unit_price ?? 0,
                usage_unit: synced.usage_unit || synced.comparable_unit || '',
                comparable_unit: synced.comparable_unit || synced.usage_unit || '',
            };
        });
        const calculatedTotal = totalAmount;

        const now = new Date().toISOString();
        const trimmedInvoiceNumber = invoiceNumber.trim();
        const trimmedDueDate = dueDate?.trim() || undefined;

        return {
            data: {
                transaction_date: finalTransactionDate,
                due_date: trimmedDueDate,
                invoice_number: trimmedInvoiceNumber || undefined,
                total_amount: calculatedTotal,
                balance_due: calculatedTotal,
                currency: currency,
                vendor: {
                    name: vendorName.trim()
                },
                ap_account: invoice?.data.ap_account,
                line_items: finalLineItems,
                billing_address: invoice?.data.billing_address,
                tax_details: { total_tax: totalTax || 0 },
                metadata: {
                    create_time: invoice?.data.metadata.create_time || now,
                    last_updated_time: now,
                    vendor_resolution: invoice?.data.metadata.vendor_resolution,
                },
                status: 'confirmed',
                extraction_method: invoice?.data.extraction_method,
                confidence_scores: invoice?.data.confidence_scores
            }
        };
    };

    const handlePostConfirmResponse = (response: DocInvoice) => {
        setInvoice(response);
        setConfirmedResponse(response);
        const increases = response?.price_increases ?? [];
        const decreases = response?.price_decreases ?? [];
        const hasChanges = increases.length > 0 || decreases.length > 0;
        if (hasChanges) {
            setPriceIncreaseItems(increases);
            setPriceDecreaseItems(decreases);
            setShowPriceChangePopup(true);
            return;
        }
        onConfirm(response);
    };

    const saveCombinedDocument = async (updateData: UpdateInvoiceRequest): Promise<DocInvoice> => {
        if (!combineTargetDocumentId || !combineSourceUpdateData) {
            throw new Error('Missing combine target document.');
        }

        const response = linkMode === 'duplicate'
            ? (isBillDocument
                ? await documentsApi.linkDuplicateBill(documentId, combineTargetDocumentId, updateData, businessId, combineSourceUpdateData)
                : (isExpenseDocument
                    ? await documentsApi.linkDuplicateExpense(documentId, combineTargetDocumentId, updateData, businessId, combineSourceUpdateData)
                    : await documentsApi.linkDuplicateInvoice(documentId, combineTargetDocumentId, updateData, businessId, combineSourceUpdateData)))
            : (isBillDocument
                ? await documentsApi.combineBillPages(documentId, combineTargetDocumentId, updateData, businessId, combineSourceUpdateData)
                : (isExpenseDocument
                    ? await documentsApi.combineExpensePages(documentId, combineTargetDocumentId, updateData, businessId, combineSourceUpdateData)
                    : await documentsApi.combineInvoicePages(documentId, combineTargetDocumentId, updateData, businessId, combineSourceUpdateData)));
        setCombineTargetDocumentId(null);
        setCombineSourceUpdateData(null);
        setLinkMode(null);
        return response;
    };

    const saveConfirmedDocument = async (updateData: UpdateInvoiceRequest) => {
        if (combineTargetDocumentId) {
            const response = await saveCombinedDocument(updateData);
            handlePostConfirmResponse(response);
            return;
        }
        let response: DocInvoice;
        if (isBillDocument) {
            response = await documentsApi.updateBill(documentId, updateData, businessId);
        } else if (isExpenseDocument) {
            response = await documentsApi.updateExpense(documentId, updateData, businessId);
        } else {
            response = await documentsApi.updateInvoice(documentId, updateData, businessId);
        }
        try {
            await persistReviewedVendorAlias();
        } catch (aliasError) {
            console.error('Failed to persist reviewed vendor alias:', aliasError);
        }
        try {
            await persistReviewedItemAliases(updateData.data.line_items || []);
        } catch (aliasError) {
            console.error('Failed to persist reviewed item aliases:', aliasError);
        }
        handlePostConfirmResponse(response);
    };

    const saveConfirmedDocumentAndSyncQbo = async (updateData: UpdateInvoiceRequest) => {
        if (combineTargetDocumentId) {
            const targetId = combineTargetDocumentId;
            await saveCombinedDocument(updateData);
            const combinedResponse = isBillDocument
                ? await documentsApi.confirmAndSyncBillToQbo(targetId, updateData, businessId)
                : await documentsApi.confirmAndSyncInvoiceToQbo(targetId, updateData, businessId);
            handlePostConfirmResponse(combinedResponse);
            return;
        }
        let response: DocInvoice;
        if (isBillDocument) {
            response = await documentsApi.confirmAndSyncBillToQbo(documentId, updateData, businessId);
        } else if (!isExpenseDocument) {
            response = await documentsApi.confirmAndSyncInvoiceToQbo(documentId, updateData, businessId);
        } else {
            await saveConfirmedDocument(updateData);
            return;
        }
        try {
            await persistReviewedVendorAlias();
        } catch (aliasError) {
            console.error('Failed to persist reviewed vendor alias:', aliasError);
        }
        try {
            await persistReviewedItemAliases(updateData.data.line_items || []);
        } catch (aliasError) {
            console.error('Failed to persist reviewed item aliases:', aliasError);
        }
        handlePostConfirmResponse(response);
    };

    const maybePromptQboSync = async (updateData: UpdateInvoiceRequest, overrideDocumentId?: string): Promise<boolean> => {
        if (!enableQboPrompt) {
            return false;
        }
        if (!isQboSyncEligibleDocument) {
            return false;
        }
        const qboDocumentId = overrideDocumentId || documentId;
        const preflight = isBillDocument
            ? await documentsApi.preflightBillQboSync(qboDocumentId, updateData, businessId)
            : await documentsApi.preflightInvoiceQboSync(qboDocumentId, updateData, businessId);
        if (preflight.should_prompt) {
            setQboPreflight(preflight);
            setPendingQboUpdateData(updateData);
            setShowQboWriteDecision(true);
            return true;
        }
        return false;
    };

    const handleBackToEditing = () => {
        setShowDuplicateDecision(false);
        setDuplicateCandidate(null);
        setRelatedDecisionType(null);
        setRelatedTargetDoc(null);
        setPendingUpdateData(null);
    };

    const handleSkipQboWrite = async () => {
        if (!pendingQboUpdateData) return;
        setLoading(true);
        setError(null);
        try {
            await saveConfirmedDocument(pendingQboUpdateData);
            setShowQboWriteDecision(false);
            setPendingQboUpdateData(null);
            setQboPreflight(null);
        } catch (err: any) {
            console.error(`Failed to save ${documentLabel} without QBO sync:`, err);
            setError(getUserFriendlyErrorMessage(err, `Failed to save ${documentLabel}`));
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmQboWrite = async () => {
        if (!pendingQboUpdateData) return;
        setLoading(true);
        setError(null);
        try {
            await saveConfirmedDocumentAndSyncQbo(pendingQboUpdateData);
            setShowQboWriteDecision(false);
            setPendingQboUpdateData(null);
            setQboPreflight(null);
        } catch (err: any) {
            console.error(`Failed to sync ${documentLabel} to QBO:`, err);
            setError(getUserFriendlyErrorMessage(err, `Failed to write ${documentLabel} to QuickBooks`));
        } finally {
            setLoading(false);
        }
    };

    const handleKeepSeparate = async () => {
        const separateUpdateData = buildUpdateData();
        if (!separateUpdateData) return;
        setLoading(true);
        setError(null);
        try {
            const prompted = await maybePromptQboSync(separateUpdateData);
            if (prompted) {
                setShowDuplicateDecision(false);
                setDuplicateCandidate(null);
                setPendingUpdateData(null);
                return;
            }
            await saveConfirmedDocument(separateUpdateData);
            setShowDuplicateDecision(false);
            setDuplicateCandidate(null);
            setPendingUpdateData(null);
        } catch (err: any) {
            console.error(`Failed to save separate ${documentLabel}:`, err);
            setError(getUserFriendlyErrorMessage(err, `Failed to save ${documentLabel}`));
        } finally {
            setLoading(false);
        }
    };

    const handleCombinePages = async () => {
        if (!pendingUpdateData || !duplicateCandidate?.id) return;
        setLoading(true);
        setError(null);
        try {
            const targetDoc = await fetchDocumentById(duplicateCandidate.id);
            const mergedPreview = buildCombinedPreviewDoc(targetDoc, pendingUpdateData);
            initFromDoc(mergedPreview);
            setCombineTargetDocumentId(duplicateCandidate.id);
            setCombineSourceUpdateData(pendingUpdateData);
            setLinkMode(isCombineDecision ? 'combine' : 'duplicate');
            setShowDuplicateDecision(false);
            setDuplicateCandidate(null);
            setRelatedDecisionType(null);
            setPendingUpdateData(null);
        } catch (err: any) {
            console.error('Failed to combine pages:', err);
            setError(getUserFriendlyErrorMessage(err, 'Failed to combine pages.'));
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        const combineSafeUpdateData = buildUpdateData();
        if (!combineSafeUpdateData) return;
        if (tutorialMode) {
            return;
        }
        setLoading(true);
        setError(null);

        try {
            if (combineTargetDocumentId) {
                const prompted = await maybePromptQboSync(combineSafeUpdateData, combineTargetDocumentId);
                if (prompted) {
                    return;
                }
                const response = await saveCombinedDocument(combineSafeUpdateData);
                handlePostConfirmResponse(response);
                return;
            }

            const duplicateResult = isBillDocument
                ? await documentsApi.checkDuplicateBill(documentId, combineSafeUpdateData, businessId)
                : (isExpenseDocument
                    ? await documentsApi.checkDuplicateExpense(documentId, combineSafeUpdateData, businessId)
                    : await documentsApi.checkDuplicateInvoice(documentId, combineSafeUpdateData, businessId));

            if (duplicateResult.exists && duplicateResult.candidate) {
                setPendingUpdateData(combineSafeUpdateData);
                setDuplicateCandidate(duplicateResult.candidate);
                setShowDuplicateDecision(true);
                return;
            }

            const prompted = await maybePromptQboSync(combineSafeUpdateData);
            if (prompted) {
                return;
            }
            await saveConfirmedDocument(combineSafeUpdateData);
        } catch (err: any) {
            console.error('Failed to confirm:', err);
            setError(getUserFriendlyErrorMessage(err, 'Failed to confirm.'));
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return createPortal(
            <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
                <div className="bg-white rounded-lg p-8 flex flex-col items-center gap-4">
                    <Loader className="w-8 h-8 animate-spin text-blue-600" />
                    <p className="text-gray-600">Loading {documentLabel} data...</p>
                </div>
            </div>,
            document.body
        );
    }

    const confidenceScores = invoice?.data.confidence_scores || {};
    const hasLowConfidence = Object.values(confidenceScores).some((score: any) =>
        typeof score === 'number' && score < 0.8
    );

    const handleClosePriceChangePopup = () => {
        setShowPriceChangePopup(false);
        setPriceIncreaseItems([]);
        setPriceDecreaseItems([]);
        onConfirm(confirmedResponse || undefined);
        setConfirmedResponse(null);
    };

    const duplicateExistingTotal = duplicateCandidate?.total_amount || 0;
    const duplicateCurrentTotal = pendingUpdateData?.data.total_amount || totalAmount;
    const duplicateCombinedTotal = Math.max(duplicateExistingTotal, duplicateCurrentTotal);
    const relatedTargetLineItems = normalizeLineItemsForForm(relatedTargetDoc?.data?.line_items || []);
    const currentDocumentLineItems = normalizeLineItemsForForm(lineItems || []);
    const isCombineDecision = relatedDecisionType === 'combine_candidate';
    const existingDuplicateRelatedCandidate = (() => {
        const candidate = getDuplicateRelatedCandidate(invoice);
        if (!candidate) return undefined;
        const status = invoice?.data?.status;
        if (status === 'confirmed' || status === 'posted_to_qbo') {
            return candidate;
        }
        return candidate.candidate_status === 'confirmed' || candidate.candidate_status === 'posted_to_qbo'
            ? candidate
            : undefined;
    })();
    const duplicateRelatedPages = formatRelatedSourcePages(existingDuplicateRelatedCandidate?.source_pages);
    const originalParsePages: Array<{ key: string; title: string; data?: Partial<InvoiceData> | null; originalFilename?: string; mergedAt?: string }> = [
        {
            key: 'current',
            title: invoice?.merged_pages?.length ? 'Primary parsed page' : 'Original parsed document',
            data: invoice?.original_parsed_data,
        },
        ...((invoice?.merged_pages || []).map((page: MergedInvoicePage, index) => ({
            key: page.source_doc_id || page.file_id || `merged-${index}`,
            title: page.original_filename || `Merged page ${index + 1}`,
            data: (page.original_parsed_data || page.data) as Partial<InvoiceData> | undefined,
            originalFilename: page.original_filename,
            mergedAt: page.merged_at,
        })))
    ].filter((page) => page.data);
    const primaryOriginalData = invoice?.original_parsed_data || originalParsePages[0]?.data;
    const originalLineItems = getRawLineItems(primaryOriginalData);
    const multiPageWarnings = (
        (((invoice?.data?.metadata as { multi_page_warnings?: Array<{ code?: string; message?: string; values?: string[] }> }) || {}).multi_page_warnings)
        || (((preFetchedInvoice?.data?.metadata as { multi_page_warnings?: Array<{ code?: string; message?: string; values?: string[] }> }) || {}).multi_page_warnings)
        || []
    ).filter(Boolean);

    const content = (
        <>
            {/* Price change popup — shown immediately after confirm when any line item had a price increase or decrease */}
            {showPriceChangePopup && (
                <div className="fixed inset-0 z-[3001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-6">
                    <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center gap-3 text-amber-700">
                                <AlertCircle className="w-8 h-8 shrink-0" />
                                <div>
                                    <h3 className="text-lg font-semibold">
                                        {priceIncreaseItems.length > 0 && priceDecreaseItems.length > 0
                                            ? 'Price changes detected'
                                            : priceIncreaseItems.length > 0
                                                ? 'Price increases detected'
                                                : 'Price decreases detected'}
                                    </h3>
                                    <p className="text-sm text-gray-600 mt-0.5">
                                        {priceIncreaseItems.length > 0 && (
                                            <>{priceIncreaseItems.length} line item{priceIncreaseItems.length !== 1 ? 's' : ''} had unit price increases.</>
                                        )}
                                        {priceIncreaseItems.length > 0 && priceDecreaseItems.length > 0 && ' '}
                                        {priceDecreaseItems.length > 0 && (
                                            <>{priceDecreaseItems.length} line item{priceDecreaseItems.length !== 1 ? 's' : ''} had unit price decreases.</>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1">
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                    <p className="font-medium text-gray-900">{vendorName}</p>
                                    {invoiceNumber && (
                                        <p className="text-sm text-gray-600 mt-0.5">• {invoiceNumber}</p>
                                    )}
                                </div>
                                {priceIncreaseItems.length > 0 && (
                                    <>
                                        <div className="px-4 py-2 bg-amber-50/50 border-b border-gray-100 text-xs font-medium text-amber-800">
                                            Price increases
                                        </div>
                                        <ul className="divide-y divide-gray-100">
                                            {priceIncreaseItems.map((item, itemIdx) => (
                                                <li key={`inc-${itemIdx}`} className="px-4 py-3 bg-amber-50/30">
                                                    <p className="font-medium text-gray-900">{item.description || 'Line item'}</p>
                                                    <p className="text-sm mt-2">
                                                        <span className="text-gray-500">Previous:</span>{' '}
                                                        <span className="line-through">
                                                            {item.currency} {item.previous_most_recent_unit_price.toFixed(2)}
                                                        </span>
                                                        {' → '}
                                                        <span className="font-medium text-amber-700">
                                                            {item.currency} {item.unit_price.toFixed(2)}
                                                        </span>
                                                    </p>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                                {priceDecreaseItems.length > 0 && (
                                    <>
                                        <div className="px-4 py-2 bg-green-50/50 border-b border-gray-100 text-xs font-medium text-green-800">
                                            Price decreases
                                        </div>
                                        <ul className="divide-y divide-gray-100">
                                            {priceDecreaseItems.map((item, itemIdx) => (
                                                <li key={`dec-${itemIdx}`} className="px-4 py-3 bg-green-50/30">
                                                    <p className="font-medium text-gray-900">{item.description || 'Line item'}</p>
                                                    <p className="text-sm mt-2">
                                                        <span className="text-gray-500">Previous:</span>{' '}
                                                        <span className="line-through">
                                                            {item.currency} {item.previous_most_recent_unit_price.toFixed(2)}
                                                        </span>
                                                        {' → '}
                                                        <span className="font-medium text-green-700">
                                                            {item.currency} {item.unit_price.toFixed(2)}
                                                        </span>
                                                    </p>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="p-6 border-t border-gray-200 flex justify-end">
                            <button
                                onClick={handleClosePriceChangePopup}
                                className="bg-blue-600 text-white font-medium py-2.5 px-6 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showQboWriteDecision && (
                <div className="fixed inset-0 z-[3001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-6">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center gap-3 text-blue-700">
                                <AlertCircle className="w-8 h-8 shrink-0" />
                                <div>
                                    <h3 className="text-lg font-semibold">Write This {documentLabel} To QuickBooks As a Bill?</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        This {documentLabel.toLowerCase()} is not in your QuickBooks account. Do you want to write it into QuickBooks as a Bill?
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="px-6 py-5 text-sm text-gray-700 space-y-2">
                            <div className="flex items-center justify-between gap-4">
                                <span className="text-gray-500">Bill Number</span>
                                <span className="font-medium text-gray-900">{pendingQboUpdateData?.data.invoice_number || 'None'}</span>
                            </div>
                            <div className="flex items-center justify-between gap-4">
                                <span className="text-gray-500">Vendor</span>
                                <span className="font-medium text-gray-900">{getDocumentVendorName(pendingQboUpdateData, 'Unknown Vendor')}</span>
                            </div>
                            <div className="flex items-center justify-between gap-4">
                                <span className="text-gray-500">Total Amount</span>
                                <span className="font-medium text-gray-900">${(pendingQboUpdateData?.data.total_amount || 0).toFixed(2)}</span>
                            </div>
                            {qboPreflight?.reason && (
                                <div className="rounded-lg bg-blue-50 text-blue-700 px-3 py-2 text-sm">
                                    {qboPreflight.reason}
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleSkipQboWrite}
                                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                                disabled={loading}
                            >
                                No, Confirm Only
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmQboWrite}
                                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                disabled={loading || qboPreflight?.can_write === false}
                            >
                                Yes, Write As Bill
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDuplicateDecision && duplicateCandidate && (
                <div className="fixed inset-0 z-[3002] flex items-center justify-center bg-black/50 backdrop-blur-sm p-6">
                    <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full overflow-hidden">
                        <div className="p-6 border-b border-gray-200">
                            <button
                                onClick={handleBackToEditing}
                                className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back to edit this {documentLabel}
                            </button>
                            <div className="flex items-start gap-3">
                                <AlertCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        {isCombineDecision
                                            ? `This looks like another page of the same ${documentLabel}`
                                            : `Possible duplicate ${documentLabel} detected`}
                                    </h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {isCombineDecision
                                            ? `We found a matching ${documentLabel} that looks like the same multi-page document. You can review the combined draft or keep reviewing only this page.`
                                            : `We found another ${documentLabel} with closely matching anchors. Compare both documents below before deciding whether to continue reviewing this one.`}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="rounded-lg border border-gray-200 p-4">
                                    <p className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">Matched saved {documentLabel}</p>
                                    <p className="mt-2 text-sm font-semibold text-gray-900">{duplicateCandidate.vendor_name || 'Unknown vendor'}</p>
                                    <p className="mt-1 text-sm text-gray-600">#{duplicateCandidate.invoice_number || 'No invoice number'}</p>
                                    {duplicateCandidate.transaction_date && (
                                        <p className="mt-1 text-xs text-gray-500">{duplicateCandidate.transaction_date}</p>
                                    )}
                                    <div className="mt-4">
                                        <p className="text-xs text-gray-500">{documentLabel.charAt(0).toUpperCase() + documentLabel.slice(1)} total amount</p>
                                        <p className="text-2xl font-semibold text-gray-900">${duplicateExistingTotal.toFixed(2)}</p>
                                    </div>
                                    <p className="mt-3 text-xs text-gray-500">
                                        {duplicateCandidate.page_count || duplicateCandidate.file_count || 1} page{(duplicateCandidate.page_count || duplicateCandidate.file_count || 1) !== 1 ? 's' : ''} already linked
                                    </p>
                                    {relatedTargetLineItems.length > 0 && (
                                        <div className="mt-4">
                                            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">Matched {documentLabel} items</p>
                                            <div className="mt-2 max-h-32 space-y-1 overflow-y-auto pr-1">
                                                {relatedTargetLineItems.map((item, itemIndex) => (
                                                    <div key={`target-line-${itemIndex}`} className="text-xs text-gray-600">
                                                        {item.description || item.raw_description || 'Line item'} • ${(item.line_amount ?? item.amount ?? 0).toFixed(2)}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="rounded-lg border border-blue-200 bg-blue-50/70 p-4">
                                    <p className="text-xs font-semibold uppercase tracking-[0.08em] text-blue-700">Current {documentLabel} from this upload</p>
                                    <p className="mt-2 text-sm font-semibold text-gray-900">{vendorName || 'Unknown vendor'}</p>
                                    <p className="mt-1 text-sm text-gray-600">#{invoiceNumber || 'No invoice number'}</p>
                                    {transactionDate && (
                                        <p className="mt-1 text-xs text-gray-500">{transactionDate}</p>
                                    )}
                                    <div className="mt-4">
                                        <p className="text-xs text-gray-500">{documentLabel.charAt(0).toUpperCase() + documentLabel.slice(1)} total amount</p>
                                        <p className="text-2xl font-semibold text-blue-700">${duplicateCurrentTotal.toFixed(2)}</p>
                                    </div>
                                    <p className="mt-3 text-xs text-gray-500">
                                        {lineItems.length} extracted item{lineItems.length !== 1 ? 's' : ''}
                                    </p>
                                    {currentDocumentLineItems.length > 0 && (
                                        <div className="mt-4">
                                            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-blue-700">Current {documentLabel} items</p>
                                            <div className="mt-2 max-h-32 space-y-1 overflow-y-auto pr-1">
                                                {currentDocumentLineItems.map((item, itemIndex) => (
                                                    <div key={`current-line-${itemIndex}`} className="text-xs text-gray-600">
                                                        {item.description || item.raw_description || 'Line item'} • ${(item.line_amount ?? item.amount ?? 0).toFixed(2)}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
                                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-amber-700">
                                    {isCombineDecision ? 'Combined preview' : 'Decision guidance'}
                                </p>
                                <div className="mt-2 flex items-center justify-between gap-4">
                                    <div>
                                        <p className="text-sm text-gray-600">
                                            {isCombineDecision
                                                ? `If you combine these pages, you will edit the existing ${documentLabel} with merged line items before the final update.`
                                                : `If these are two copies of the same ${documentLabel}, do not combine them. Inspect both sides, then continue reviewing only this ${documentLabel} if you still want to save it separately.`}
                                        </p>
                                        <p className="mt-1 text-xs text-gray-500">
                                            {isCombineDecision
                                                ? `Combined draft: ${(duplicateCandidate.page_count || 1) + 1} page view, ${lineItems.length} new item${lineItems.length !== 1 ? 's' : ''} added`
                                                : 'Use the comparison above to confirm whether this is a true duplicate before saving.'}
                                        </p>
                                    </div>
                                    {isCombineDecision && (
                                        <div className="rounded-lg bg-white px-4 py-3 text-right shadow-sm ring-1 ring-amber-200">
                                            <p className="text-xs text-gray-500">Highlighted total to confirm</p>
                                            <p className="text-2xl font-semibold text-amber-700">${duplicateCombinedTotal.toFixed(2)}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200 flex flex-wrap justify-end gap-3">
                            <button
                                onClick={handleBackToEditing}
                                className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium py-2.5 px-5 rounded-lg text-sm"
                            >
                                Back to edit
                            </button>
                            {isCombineDecision ? (
                                <>
                                    <button
                                        onClick={handleKeepSeparate}
                                        disabled={loading}
                                        className="bg-white border border-blue-300 text-blue-700 hover:bg-blue-50 transition-colors font-medium py-2.5 px-5 rounded-lg text-sm"
                                    >
                                        Save as separate {documentLabel}
                                    </button>
                                    <button
                                        onClick={handleCombinePages}
                                        disabled={loading}
                                        className="bg-amber-600 text-white hover:bg-amber-700 transition-colors font-medium py-2.5 px-5 rounded-lg text-sm flex items-center gap-2"
                                    >
                                        {loading ? <Loader className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />}
                                        Review combined {documentLabel}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={handleKeepSeparate}
                                        disabled={loading}
                                        className="bg-white border border-blue-300 text-blue-700 hover:bg-blue-50 transition-colors font-medium py-2.5 px-5 rounded-lg text-sm"
                                    >
                                        Save as separate {documentLabel}
                                    </button>
                                    <button
                                        onClick={handleCombinePages}
                                        disabled={loading}
                                        className="bg-amber-600 text-white hover:bg-amber-700 transition-colors font-medium py-2.5 px-5 rounded-lg text-sm flex items-center gap-2"
                                    >
                                        {loading ? <Loader className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />}
                                        Review linked {documentLabel}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div
                className={embedded ? "h-full min-h-0" : "fixed inset-0 z-[3000] overflow-hidden"}
                onClick={embedded || tutorialMode ? undefined : onClose}
            >
                {!embedded && <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />}
                <div className={embedded ? "" : "absolute inset-0 overflow-hidden"}>
                    <div className={embedded ? "" : "fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-12 pointer-events-none"}>
                        <div
                            className={embedded
                                ? "bg-white relative flex h-full min-h-0 flex-col overflow-hidden shadow-xl"
                                : "pointer-events-auto relative flex h-full w-screen max-w-[1600px] flex-col overflow-hidden bg-white shadow-2xl lg:w-[80vw] lg:min-w-[1180px]"}
                            onClick={embedded ? undefined : (e) => e.stopPropagation()}
                        >
                            {/* Close Button — just close, leave invoice pending */}
                            {!tutorialMode && (
                                <button
                                    onClick={onClose}
                                    className={embedded ? "absolute right-4 top-4 z-10 text-gray-400 transition-colors hover:text-gray-600 sm:right-5 sm:top-5" : "absolute right-4 top-4 z-10 text-gray-400 transition-colors hover:text-gray-600 sm:right-6 sm:top-6"}
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            )}

                            {/* Header */}
                            <div className={embedded ? "border-b border-gray-200 px-4 py-4 sm:px-6 sm:py-5" : "border-b border-gray-200 px-4 py-4 sm:px-8 sm:py-6"}>
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0 pr-8">
                                        <h2 className="mb-2 text-lg font-semibold text-gray-900 sm:text-[24px]">
                                            {combineTargetDocumentId
                                                ? `${linkMode === 'duplicate' ? 'Review Linked' : 'Review Combined'} ${documentLabel.charAt(0).toUpperCase() + documentLabel.slice(1)}`
                                                : `Confirm ${documentLabel.charAt(0).toUpperCase() + documentLabel.slice(1)} Information`}
                                        </h2>
                                        <p className="text-sm text-gray-600">
                                            {combineTargetDocumentId
                                                ? `You are reviewing the ${linkMode === 'duplicate' ? 'linked' : 'combined'} ${documentLabel} draft before applying the final update.`
                                                : `Review and edit the extracted ${documentLabel} data before confirming`}
                                        </p>
                                    </div>
                                    <div className="mr-8 hidden items-center gap-3 sm:flex">
                                        {embedded && (
                                            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                                                <button
                                                    type="button"
                                                    onClick={() => setEditorView('cleaned')}
                                                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${editorView === 'cleaned' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                                                >
                                                    Cleaned
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setEditorView('original')}
                                                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${editorView === 'original' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                                                >
                                                    Original
                                                </button>
                                            </div>
                                        )}
                                        {!embedded && (
                                            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                                                <button
                                                    type="button"
                                                    onClick={() => setEditorView('cleaned')}
                                                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${editorView === 'cleaned' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                                                >
                                                    Cleaned
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setEditorView('original')}
                                                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${editorView === 'original' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                                                >
                                                    Original
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-2 flex items-center gap-2 text-blue-600 bg-blue-50 p-2 rounded-lg text-xs">
                                    <AlertCircle className="w-3 h-3" />
                                    <span><strong>Priority:</strong> Your edits will override the parsed data. Parsed data is shown below for reference.</span>
                                </div>
                                {hasLowConfidence && (
                                    <div className="mt-3 flex items-center gap-2 text-amber-600 bg-amber-50 p-2 rounded-lg text-sm">
                                        <AlertCircle className="w-4 h-4" />
                                        <span>Some fields have low confidence scores. Please verify carefully.</span>
                                    </div>
                                )}
                                {multiPageWarnings.length > 0 && (
                                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                                        <div className="flex items-start gap-2 text-amber-800">
                                            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium">This upload may contain conflicting page data.</p>
                                                <div className="mt-2 space-y-2">
                                                    {multiPageWarnings.map((warning, index) => (
                                                        <div key={`${warning.code || 'multi-page-warning'}-${index}`} className="text-xs text-amber-900">
                                                            <p>{warning.message || 'Pages in this upload appear inconsistent.'}</p>
                                                            {Array.isArray(warning.values) && warning.values.length > 0 && (
                                                                <p className="mt-1 text-amber-700">
                                                                    Detected values: {warning.values.join(' / ')}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {combineTargetDocumentId && (
                                    <div className="mt-3 flex items-center gap-2 text-amber-800 bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm">
                                        <GitMerge className="w-4 h-4" />
                                        <span>
                                            <strong>Reviewing {linkMode === 'duplicate' ? 'linked' : 'combined'} {documentLabel}:</strong>
                                            {' '}
                                            {linkMode === 'duplicate'
                                                ? 'your final confirm will keep one canonical document and link the duplicate source into it.'
                                                : 'your final confirm will update the existing document and merge this page into it.'}
                                        </span>
                                    </div>
                                )}
                                {!combineTargetDocumentId && !showDuplicateDecision && existingDuplicateRelatedCandidate && (
                                    <div className="mt-3 flex items-center gap-2 text-amber-800 bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm">
                                        <AlertCircle className="w-4 h-4" />
                                        <span>
                                            <strong>Possible duplicate detected:</strong> you are reviewing only this {documentLabel}.
                                            {duplicateRelatedPages ? ` A matching document was found on ${duplicateRelatedPages}.` : ' A matching document was found.'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className={embedded ? "grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto px-4 pb-4 pt-4 lg:grid-cols-[minmax(0,3fr)_minmax(320px,2fr)] lg:gap-6 lg:overflow-hidden lg:px-6 lg:pb-6" : "grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto px-4 pb-4 pt-4 sm:px-6 lg:grid-cols-[minmax(0,3fr)_minmax(320px,2fr)] lg:gap-6 lg:overflow-hidden lg:px-8 lg:pb-6 lg:pt-6"}>
                                <div className={embedded ? "order-2 min-h-0 lg:order-1 lg:overflow-y-auto" : "order-2 min-h-0 lg:order-1 lg:overflow-y-auto"}>
                                    {error && (
                                        <div className={`bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm flex items-center gap-2 ${embedded ? '' : ''}`}>
                                            <AlertCircle className="w-4 h-4" />
                                            {error}
                                        </div>
                                    )}

                                    {/* Form Fields */}
                                    {editorView === 'original' ? (
                                        <div className="space-y-6">
                                            <div className="grid gap-4 xl:grid-cols-4 md:grid-cols-2">
                                                <div className="rounded-xl border border-gray-200 bg-white p-4">
                                                    <p className="text-xs font-medium uppercase tracking-[0.08em] text-gray-500">{documentType === 'invoice' ? 'Customer' : 'Vendor'} Name</p>
                                                    <p className="mt-2 text-lg font-semibold text-gray-900">{getInvoiceDataVendorName(primaryOriginalData, '—') || '—'}</p>
                                                </div>
                                                <div className="rounded-xl border border-gray-200 bg-white p-4">
                                                    <p className="text-xs font-medium uppercase tracking-[0.08em] text-gray-500">Invoice Number</p>
                                                    <p className="mt-2 text-lg font-semibold text-gray-900">{primaryOriginalData?.invoice_number || '—'}</p>
                                                </div>
                                                <div className="rounded-xl border border-gray-200 bg-white p-4">
                                                    <p className="text-xs font-medium uppercase tracking-[0.08em] text-gray-500">Items</p>
                                                    <p className="mt-2 text-lg font-semibold text-gray-900">{originalLineItems.length}</p>
                                                </div>
                                                <div className="rounded-xl border border-gray-200 bg-white p-4">
                                                    <p className="text-xs font-medium uppercase tracking-[0.08em] text-gray-500">Total</p>
                                                    <p className="mt-2 text-lg font-semibold text-gray-900">${summarizeRawAmount(primaryOriginalData).toFixed(2)}</p>
                                                </div>
                                            </div>
                                            <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                                                <div className="grid grid-cols-[56px_minmax(220px,1.6fr)_120px_100px_120px_100px_120px] border-b border-gray-200 bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                                                    <div>#</div>
                                                    <div>Item Name</div>
                                                    <div>Brand</div>
                                                    <div>Unit</div>
                                                    <div>Unit Price</div>
                                                    <div>Qty</div>
                                                    <div>Total</div>
                                                </div>
                                                <div className="max-h-[52vh] overflow-y-auto">
                                                    {originalLineItems.length === 0 ? (
                                                        <div className="px-4 py-8 text-sm text-gray-500">No original parsed line items available.</div>
                                                    ) : originalLineItems.map((item, index) => (
                                                        <div key={`original-item-${index}`} className="grid grid-cols-[56px_minmax(220px,1.6fr)_120px_100px_120px_100px_120px] border-b border-gray-100 px-4 py-3 text-sm text-gray-700 last:border-b-0">
                                                            <div>{index + 1}</div>
                                                            <div className="font-medium text-gray-900">{item.description || item.raw_description || 'Line item'}</div>
                                                            <div>{item.brand_name || '—'}</div>
                                                            <div>{item.unit || '—'}</div>
                                                            <div>${(item.unit_price || item.amount || 0).toFixed(2)}</div>
                                                            <div>{item.quantity || 1}</div>
                                                            <div className="font-medium text-gray-900">${(item.amount || 0).toFixed(2)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-6 overflow-y-auto flex-1 pr-2">
                                            <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                                                <div className="mb-4">
                                                    <h3 className="text-base font-semibold text-gray-900">Basic Information</h3>
                                                    <p className="mt-1 text-sm text-gray-500">Review the cleaned fields before saving this {documentLabel}.</p>
                                                </div>
                                                <div className="grid gap-4 md:grid-cols-2">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-900 mb-1">
                                                            {documentType === 'invoice' ? 'Customer' : 'Vendor'} Name <span className="text-red-500">*</span>
                                                            {confidenceScores.vendor_name && (
                                                                <span className={`ml-2 text-xs ${(confidenceScores.vendor_name as number) >= 0.8 ? 'text-green-600' : 'text-amber-600'}`}>
                                                                    {((confidenceScores.vendor_name as number) * 100).toFixed(0)}%
                                                                </span>
                                                            )}
                                                        </label>
                                                        <SearchableSuggestionInput
                                                            value={vendorName}
                                                            options={vendorOptions}
                                                            onChangeText={setVendorName}
                                                            onSelectOption={(option) => setVendorName(option.name)}
                                                            placeholder={`Enter or search ${documentType === 'invoice' ? 'customer' : 'vendor'} name`}
                                                            inputClassName="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-900 mb-1">
                                                            Invoice Number
                                                            {confidenceScores.invoice_number && (
                                                                <span className={`ml-2 text-xs ${(confidenceScores.invoice_number as number) >= 0.8 ? 'text-green-600' : 'text-amber-600'}`}>
                                                                    {((confidenceScores.invoice_number as number) * 100).toFixed(0)}%
                                                                </span>
                                                            )}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={invoiceNumber}
                                                            onChange={(e) => setInvoiceNumber(e.target.value)}
                                                            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            placeholder="Enter invoice number"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-900 mb-1">
                                                            Transaction Date <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="date"
                                                            value={transactionDate}
                                                            onChange={(e) => setTransactionDate(e.target.value)}
                                                            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-900 mb-1">
                                                            Due Date
                                                        </label>
                                                        <input
                                                            type="date"
                                                            value={dueDate}
                                                            onChange={(e) => setDueDate(e.target.value)}
                                                            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-900 mb-1">
                                                            Total Amount <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={totalAmount}
                                                            onChange={(e) => {
                                                                const val = parseFloat(e.target.value) || 0;
                                                                setTotalAmount(val);
                                                            }}
                                                            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-900 mb-1">
                                                            Total Tax
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={totalTax}
                                                            onChange={(e) => {
                                                                const val = parseFloat(e.target.value) || 0;
                                                                setTotalTax(val);
                                                            }}
                                                            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                            </section>

                                            <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                                                <div className="mb-4 flex items-start justify-between gap-4">
                                                    <div>
                                                        <h3 className="text-base font-semibold text-gray-900">Line Items</h3>
                                                        <p className="mt-1 text-sm text-gray-500">
                                                            ${lineItems.reduce((sum, item) => sum + (item.amount || 0), 0).toFixed(2)} total across {lineItems.length} item{lineItems.length !== 1 ? 's' : ''}
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={handleAddLineItem}
                                                        className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                        Add Item
                                                    </button>
                                                </div>
                                                <div className="relative mb-4">
                                                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                                    <input
                                                        type="text"
                                                        value={lineItemSearch}
                                                        onChange={(e) => setLineItemSearch(e.target.value)}
                                                        placeholder="Search items, size, unit, unit price, or total..."
                                                        className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-9 pr-4 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    />
                                                </div>
                                                <div className="space-y-3">
                                                    {lineItems.length === 0 ? (
                                                        <p className="text-sm text-gray-500 text-center py-4">
                                                            No line items. Click "Add Item" to add one.
                                                        </p>
                                                    ) : filteredLineItems.length === 0 ? (
                                                        <p className="text-sm text-gray-500 text-center py-4">
                                                            No line items match your search.
                                                        </p>
                                                    ) : (
                                                        <>
                                                            {filteredLineItems.map(({ item, index }) => (
                                                                <div key={index} className="rounded-2xl border border-gray-200 bg-gray-50/70 p-3">
                                                                    <div className="grid items-start gap-2.5 md:grid-cols-[minmax(150px,1fr)_minmax(0,1.1fr)_minmax(120px,0.7fr)_140px_28px]">
                                                                        <div className="min-w-0">
                                                                            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500">Raw Invoice Item</p>
                                                                            <p className="mt-1 truncate text-[13px] font-medium leading-5 text-gray-900" title={item.raw_description || item.description || 'Line item'}>
                                                                                {item.raw_description || item.description || 'Line item'}
                                                                            </p>
                                                                        </div>
                                                                        <div className="min-w-0">
                                                                            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500">
                                                                                Clean Item Name
                                                                            </label>
                                                                            <SearchableSuggestionInput
                                                                                value={item.description || ''}
                                                                                options={getFilteredInventoryOptions(index).map((candidate) => ({
                                                                                    id: candidate._id,
                                                                                    name: formatInventoryItemDisplayName(candidate),
                                                                                    subtitle: (candidate.search_keys || []).slice(0, 3).join(' • ') || undefined,
                                                                                }))}
                                                                                onChangeText={(nextValue) => handleLineItemChange(index, 'description', nextValue)}
                                                                                onSelectOption={(option) => {
                                                                                    handleLineItemChange(index, 'description', option.name);
                                                                                    if (option.id !== '__typed__') {
                                                                                        handleInventorySuggestionSelect(index, option.id);
                                                                                    }
                                                                                }}
                                                                                placeholder="Clean item name"
                                                                                inputClassName="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-3 text-[13px] leading-5 text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                            />
                                                                        </div>
                                                                        <div className="min-w-0">
                                                                            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500">
                                                                                Brand Name
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                value={item.brand_name || ''}
                                                                                onChange={(e) => handleLineItemChange(index, 'brand_name', e.target.value)}
                                                                                placeholder="Optional brand"
                                                                                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13px] leading-5 text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                            />
                                                                        </div>
                                                                        <div className="min-w-0">
                                                                            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500">Unit Price</label>
                                                                            <input
                                                                                type="text"
                                                                                readOnly
                                                                                value={typeof item.usage_unit_price === 'number'
                                                                                    ? `$${item.usage_unit_price.toFixed(2)}${String(item.usage_unit || item.comparable_unit || '').trim() ? ` ${String(item.usage_unit || item.comparable_unit || '').toLowerCase()}` : ''}`
                                                                                    : ''}
                                                                                placeholder="—"
                                                                                className="w-full rounded-lg border border-gray-300 bg-gray-50 px-2.5 py-2 text-[13px] leading-5 text-gray-700 focus:outline-none"
                                                                            />
                                                                        </div>
                                                                        <button
                                                                            onClick={() => handleRemoveLineItem(index)}
                                                                            className="mt-4 rounded p-1.5 text-red-600 hover:bg-red-50 hover:text-red-700"
                                                                            title="Remove line item"
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </button>
                                                                    </div>

                                                                    <div className="mt-3 grid gap-2 md:grid-cols-[3.5fr_3.5fr_2fr_1fr]">
                                                                        <div className="min-w-0">
                                                                            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500">Purchase Size</label>
                                                                            <div className="grid grid-cols-[20%_40%_40%] overflow-hidden rounded-lg border border-gray-300 bg-white">
                                                                                <div className="border-r border-gray-200">
                                                                                    <input
                                                                                        type="number"
                                                                                        step="0.01"
                                                                                        value={item.purchase_quantity ?? item.quantity ?? 1}
                                                                                        onChange={(e) => handleLineItemChange(index, 'purchase_quantity', parseFloat(e.target.value) || 1)}
                                                                                        placeholder="Qty"
                                                                                        className="w-full border-0 bg-transparent px-1.5 py-1.5 text-[12px] [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                                                    />
                                                                                </div>
                                                                                <div className="border-r border-gray-200">
                                                                                    <select
                                                                                        value={item.purchase_unit || item.unit || ''}
                                                                                        onChange={(e) => handleLineItemChange(index, 'purchase_unit', e.target.value)}
                                                                                        className="w-full border-0 bg-transparent px-1.5 py-1.5 text-[12px] text-gray-900 focus:outline-none"
                                                                                        style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundImage: 'none' }}
                                                                                    >
                                                                                        {BASIC_UNIT_OPTIONS.map((unit) => (
                                                                                            <option key={unit || 'empty'} value={unit}>
                                                                                                {unit || 'Unit'}
                                                                                            </option>
                                                                                        ))}
                                                                                    </select>
                                                                                </div>
                                                                                <div className="flex items-center">
                                                                                    <span className="px-1.5 text-[10px] text-gray-500">$</span>
                                                                                    <input
                                                                                        type="number"
                                                                                        step="0.01"
                                                                                        value={item.purchase_unit_price ?? item.unit_price ?? 0}
                                                                                        onChange={(e) => handleLineItemChange(index, 'purchase_unit_price', parseFloat(e.target.value) || 0)}
                                                                                        placeholder="Price"
                                                                                        className="w-full border-0 bg-transparent py-1.5 pr-1.5 text-[12px] [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="min-w-0">
                                                                            <div className="mb-1 flex items-center justify-between gap-2">
                                                                                <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500">Packaging</label>
                                                                                <span className="truncate text-[11px] font-medium text-gray-500">
                                                                                    {formatPackagingSummary(item)}
                                                                                </span>
                                                                            </div>
                                                                            <div className="grid grid-cols-[20%_40%_40%] overflow-hidden rounded-lg border border-gray-300 bg-white">
                                                                                <div className="border-r border-gray-200">
                                                                                    <input
                                                                                        type="number"
                                                                                        step="0.01"
                                                                                        value={item.pack_count ?? ''}
                                                                                        onChange={(e) => handleLineItemChange(index, 'pack_count', parseFloat(e.target.value) || 0)}
                                                                                        placeholder="Count"
                                                                                        className="w-full border-0 bg-transparent px-1.5 py-1.5 text-[12px] [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"

                                                                                    />
                                                                                </div>

                                                                                <div className="border-r border-gray-200">
                                                                                    <input
                                                                                        type="number"
                                                                                        step="0.01"
                                                                                        value={item.pack_size ?? ''}
                                                                                        onChange={(e) => handleLineItemChange(index, 'pack_size', parseFloat(e.target.value) || 0)}
                                                                                        placeholder="Size"
                                                                                        className="w-full border-0 bg-transparent px-1.5 py-1.5 text-[12px] [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <select
                                                                                        value={item.pack_unit || ''}
                                                                                        onChange={(e) => handleLineItemChange(index, 'pack_unit', e.target.value)}
                                                                                        className="w-full border-0 bg-transparent px-1.5 py-1.5 text-[12px] text-gray-900 focus:outline-none"
                                                                                        style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundImage: 'none' }}
                                                                                    >
                                                                                        {BASIC_UNIT_OPTIONS.map((unit) => (
                                                                                            <option key={`pack-${unit || 'empty'}`} value={unit}>
                                                                                                {unit || 'Unit'}
                                                                                            </option>
                                                                                        ))}
                                                                                    </select>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="min-w-0">
                                                                            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500">Total Size</label>
                                                                            <div className="grid grid-cols-[58%_42%] overflow-hidden rounded-lg border border-gray-300 bg-white">
                                                                                <div className="border-r border-gray-200">
                                                                                    <input
                                                                                        type="number"
                                                                                        step="0.01"
                                                                                        value={item.usage_quantity ?? ''}
                                                                                        onChange={(e) => handleLineItemChange(index, 'usage_quantity', parseFloat(e.target.value) || 0)}
                                                                                        placeholder="Qty"
                                                                                        className="w-full border-0 bg-transparent px-1.5 py-1.5 text-[12px] [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <select
                                                                                        value={item.usage_unit || ''}
                                                                                        onChange={(e) => handleLineItemChange(index, 'usage_unit', e.target.value)}
                                                                                        className="w-full border-0 bg-transparent px-1.5 py-1.5 text-[12px] text-gray-900 focus:outline-none"
                                                                                        style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundImage: 'none' }}
                                                                                    >
                                                                                        {BASIC_UNIT_OPTIONS.map((unit) => (
                                                                                            <option key={`usage-${unit || 'empty'}`} value={unit}>
                                                                                                {unit || 'Unit'}
                                                                                            </option>
                                                                                        ))}
                                                                                    </select>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="min-w-0">
                                                                            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500">Total</label>
                                                                            <div className="w-full max-w-full flex items-center overflow-hidden rounded-lg border border-gray-300 bg-white focus-within:ring-1 focus-within:ring-blue-500">
                                                                                <span className="px-1.5 text-[10px] text-gray-500">$</span>
                                                                                <input
                                                                                    type="number"
                                                                                    step="0.01"
                                                                                    value={item.line_amount ?? item.amount ?? 0}
                                                                                    onChange={(e) => handleLineItemChange(index, 'line_amount', parseFloat(e.target.value) || 0)}
                                                                                    className="min-w-0 w-full border-0 py-1.5 pr-1 text-[12px] [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </>
                                                    )}
                                                </div>
                                            </section>
                                        </div>
                                    )}

                                </div>
                                <div data-tutorial={tutorialPreviewTarget} className="order-1 h-[48vh] min-h-[320px] overflow-hidden rounded-2xl border border-gray-200 bg-[#f5f5f6] lg:order-2 lg:h-auto lg:min-h-0">
                                    <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900">Document Preview</h3>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (shouldUseSourcePagination) {
                                                        const previous = previewSourceOptions[Math.max(0, activePreviewSourceIndex - 1)];
                                                        if (previous) setActivePreviewSourceKey(previous.key);
                                                        return;
                                                    }
                                                    setPreviewPageNumber((current) => Math.max(1, current - 1));
                                                }}
                                                disabled={shouldUseSourcePagination ? activePreviewSourceIndex <= 0 : previewPageNumber <= 1}
                                                className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
                                                aria-label="Previous page"
                                            >
                                                <ChevronLeft className="h-4 w-4" />
                                            </button>
                                            <span className="rounded-md bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 ring-1 ring-gray-200">
                                                {shouldUseSourcePagination ? 'Image' : 'Page'} {displayedPreviewPosition} / {displayedPreviewCount}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (shouldUseSourcePagination) {
                                                        const next = previewSourceOptions[Math.min(previewSourceOptions.length - 1, activePreviewSourceIndex + 1)];
                                                        if (next) setActivePreviewSourceKey(next.key);
                                                        return;
                                                    }
                                                    setPreviewPageNumber((current) => Math.min(previewPageCount, current + 1));
                                                }}
                                                disabled={shouldUseSourcePagination ? activePreviewSourceIndex >= (previewSourceOptions.length - 1) : previewPageNumber >= previewPageCount}
                                                className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
                                                aria-label="Next page"
                                            >
                                                <ChevronRight className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="relative h-[calc(100%-65px)] bg-[#f3f3f4]">
                                        <div ref={previewViewportRef} className="h-full overflow-auto">
                                            {previewLoading ? (
                                                <div className="flex h-full items-center justify-center text-sm text-gray-500">
                                                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                                                    Loading preview...
                                                </div>
                                            ) : previewUrl && !previewRenderError ? (
                                                <>
                                                    <div className="min-h-full min-w-full p-4">
                                                        <div className={`flex min-h-full min-w-full ${tutorialMode ? 'items-center justify-center' : 'items-start justify-start'}`}>
                                                            <div className="shrink-0">
                                                                {previewMimeType?.startsWith('image/') ? (
                                                                    <div
                                                                        className={`relative flex ${tutorialMode ? 'items-center justify-center' : 'items-start justify-center'}`}
                                                                        style={previewImageFrameStyle}
                                                                    >
                                                                        <img
                                                                            src={previewUrl}
                                                                            alt={`${documentLabel} preview`}
                                                                            className="block max-w-none rounded-xl border border-gray-200 bg-white shadow-sm"
                                                                            style={previewImageDimensions ? {
                                                                                width: `${previewImageDimensions.width}px`,
                                                                                height: `${previewImageDimensions.height}px`,
                                                                                transform: `rotate(${previewRotation}deg)`,
                                                                                transformOrigin: 'center center',
                                                                            } : {
                                                                                width: '100%',
                                                                                height: 'auto',
                                                                            }}
                                                                            onLoad={(event) => {
                                                                                const target = event.currentTarget;
                                                                                setPreviewImageNaturalSize({
                                                                                    width: target.naturalWidth,
                                                                                    height: target.naturalHeight,
                                                                                });
                                                                            }}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        className={`relative flex ${tutorialMode ? 'items-center justify-center' : 'items-start justify-center'}`}
                                                                        style={previewCanvasFrameStyle}
                                                                    >
                                                                        <canvas
                                                                            ref={previewCanvasRef}
                                                                            aria-label={`${documentLabel} preview`}
                                                                            className="block rounded-xl border border-gray-200 bg-white shadow-sm"
                                                                            style={{
                                                                                transform: `rotate(${previewRotation}deg)`,
                                                                                transformOrigin: 'center center',
                                                                            }}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-500">
                                                    {previewRenderError || 'No preview available for this document yet.'}
                                                </div>
                                            )}
                                        </div>
                                        <div className="pointer-events-none absolute bottom-3 left-3">
                                            <div className="pointer-events-auto flex items-center gap-1 rounded-lg border border-gray-200 bg-white/95 px-2 py-1.5 shadow-sm backdrop-blur">
                                                <button
                                                    type="button"
                                                    onClick={() => setPreviewZoom((current) => Math.max(0.5, Number((current - 0.1).toFixed(2))))}
                                                    className="rounded-md p-1 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                                                    aria-label="Zoom out"
                                                >
                                                    <ZoomOut className="h-4 w-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setPreviewZoom((current) => Math.min(6, Number((current + 0.1).toFixed(2))))}
                                                    className="rounded-md p-1 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                                                    aria-label="Zoom in"
                                                >
                                                    <ZoomIn className="h-4 w-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setPreviewZoom(1)}
                                                    className="ml-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                                                >
                                                    {previewMimeType?.startsWith('image/')
                                                        ? `${previewImageDimensions?.percent ?? Math.round(previewZoom * 100)}%`
                                                        : `Fit · ${Math.round(previewZoom * 100)}%`}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={cyclePreviewRotation}
                                                    className="rounded-md border border-gray-200 p-1.5 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                                    aria-label={`Rotate preview (${previewRotation} degrees)`}
                                                    title={`Rotate (${previewRotation}°)`}
                                                >
                                                    <RotateCw className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="sticky bottom-0 z-10 border-t border-gray-200 bg-white px-4 py-3 sm:px-8 sm:py-4">
                                <div className="flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
                                    <button
                                        onClick={tutorialMode ? undefined : onClose}
                                        className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleConfirm}
                                        disabled={loading || !vendorName || totalAmount < 0}
                                        className={`flex items-center justify-center gap-2 rounded-lg px-7 py-2.5 text-sm font-medium transition-colors ${!loading && vendorName && totalAmount >= 0
                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            }`}
                                    >
                                        {loading && <Loader className="w-4 h-4 animate-spin" />}
                                        {!loading && <CheckCircle2 className="w-4 h-4" />}
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    if (embedded) {
        return content;
    }

    return createPortal(content, document.body);
};

export default InvoiceConfirmationModal;
